import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Download } from 'lucide-react';
import WhatsAppButton from './components/WhatsAppButton';

const questions = [
{ type:'style', text:"When the other side rejects my proposal outright, I tend to:", options:[
    {text:"Wonder what I can give up to keep the deal alive.",map:'yielder'},
    {text:"Pause and reassess my proposal before I say anything else.",map:'calculator'},
    {text:"Push back and explain why my position makes sense.",map:'dominator'},
    {text:"Ask them to explain what specifically is not working.",map:'integrator'},
]},

{ type:'style', text:"Before entering an important negotiation, I tend to:", options:[
    {text:"Consider what outcome would feel fair and workable on both sides.",map:'integrator'},
    {text:"Define the outcome I expect to secure.",map:'dominator'},
    {text:"Review my numbers and run through my tactics one more time.",map:'calculator'},
    {text:"Think about how to keep the interaction constructive and positive.",map:'yielder'},
]},

{ type:'style', text:"When my team is competing with another team for the same limited budget, I tend to:", options:[
    {text:"Analyse the expected returns to determine the most logical split.",map:'calculator'},
    {text:"Protect the working relationship even if it means accepting a smaller share.",map:'yielder'},
    {text:"Fight for the share my team needs to succeed.",map:'dominator'},
    {text:"Search for a structure that gives both teams what they actually need.",map:'integrator'},
]},

{ type:'shadow', text:"Before a negotiation begins, the other side shares something personal about their situation. I tend to:", options:[
    {text:"Listen politely but keep my focus on the business at hand.",shadow:false},
    {text:"Think how that detail could be useful for leverage later.",shadow:true},
    {text:"Share something personal back because real connection matters.",shadow:false},
    {text:"Acknowledge it briefly and steer back to the agenda.",shadow:false},
]},

{ type:'style', text:"When a negotiation reaches deadlock I tend to:", options:[
    {text:"Offer a small concession to restart momentum.",map:'yielder'},
    {text:"Push harder and add some pressure to break the deadlock.",map:'dominator'},
    {text:"Step back and reassess the facts before moving forward.",map:'calculator'},
    {text:"Ask questions to uncover what is really blocking agreement.",map:'integrator'},
]},

{ type:'style', text:"If the other side gets angry or raises their voice during a negotiation, I tend to:", options:[
    {text:"Slow the pace and refocus on the key facts.",map:'calculator'},
    {text:"Ease off the pressure point and find calmer ground.",map:'yielder'},
    {text:"Hold my position and make it clear that the terms are not changing.",map:'dominator'},
    {text:"Acknowledge what they are feeling and redirect toward the underlying concern.",map:'integrator'},
]},

{ type:'style', text:"When I realise I have significantly more leverage than the other side, I tend to:", options:[
    {text:"Hold back from using it because the relationship matters more than the margin.",map:'yielder'},
    {text:"Reassess what this really allows me to secure.",map:'calculator'},
    {text:"Use it to shape a win-win deal that works well for both sides.",map:'integrator'},
    {text:"Press my advantage to improve the outcome I want.",map:'dominator'},
]},

{ type:'shadow', text:"If I discover the other side has made a calculation error that works in my favour, I tend to:", options:[
    {text:"Point it out immediately because I want a clean deal.",shadow:false},
    {text:"Mention it to build trust and bank goodwill for later.",shadow:false},
    {text:"Keep quiet and act surprised if they ever notice.",shadow:true},
    {text:"Say nothing because checking their own numbers is their responsibility.",shadow:false},
]},

{ type:'style', text:"When a client asks me to deliver earlier than agreed and the issue is capacity not cost, I tend to:", options:[
    {text:"Ask what they actually need by the earlier date and whether a partial solution works.",map:'integrator'},
    {text:"Reorganise my priorities to accommodate them because the relationship matters.",map:'yielder'},
    {text:"Assess capacity limits and present realistic options based on the data.",map:'calculator'},
    {text:"Push back and remind them the original timeline was agreed for good reasons.",map:'dominator'},
]},

{ type:'style', text:"When the other side makes a small concession during a negotiation, I tend to:", options:[
    {text:"Consider what that concession really changes in the deal.",map:'calculator'},
    {text:"Thank them and offer a concession in return.",map:'yielder'},
    {text:"Note it and move straight to the next item I want to secure.",map:'dominator'},
    {text:"Build on it by suggesting a trade that moves us closer to agreement.",map:'integrator'},
]},

{ type:'style', text:"When negotiating with someone I will likely work with for years, I tend to:", options:[
    {text:"Negotiate just as firmly because the tone I set now shapes future interactions.",map:'dominator'},
    {text:"Examine the long term risk exposure before committing to any position.",map:'calculator'},
    {text:"Prioritise long term stability over short term gains.",map:'yielder'},
    {text:"Look for terms both sides can operate under sustainably.",map:'integrator'},
]},

{ type:'shadow', text:"When the other side clearly trusts me completely, I tend to:", options:[
    {text:"Treat the negotiation the same way regardless of how much they trust me.",shadow:false},
    {text:"Use that trust to quietly steer the outcome more in my favour.",shadow:true},
    {text:"Use that trust to move toward a deal that genuinely works for both sides.",shadow:false},
    {text:"Appreciate the trust and make sure I do not take advantage of it.",shadow:false},
]},

{ type:'style', text:"When a colleague asks for my advice before their negotiation, I tend to say:", options:[
    {text:"Build a positive relationship early because people share more when they feel respected.",map:'yielder'},
    {text:"Understand the other side's priorities so you can propose something creative.",map:'integrator'},
    {text:"Prepare your numbers thoroughly because the other side will challenge every claim.",map:'calculator'},
    {text:"Know exactly what you want and go get it.",map:'dominator'},
]},

{ type:'style', text:"When I am offered a deal that is acceptable but not ideal, I tend to:", options:[
    {text:"Benchmark it against my alternatives before committing.",map:'calculator'},
    {text:"Push for better terms because acceptable is not good enough.",map:'dominator'},
    {text:"Lock it in because a solid deal in hand has real value.",map:'yielder'},
    {text:"Propose targeted adjustments that could improve it for both sides.",map:'integrator'},
]},

{ type:'style', text:"When the other side tells me six months later that the agreed terms are not working for them, I tend to:", options:[
    {text:"Explore specific changes that could make it workable for both sides going forward.",map:'integrator'},
    {text:"Re-evaluate the original terms against the actual data from the last six months before responding.",map:'calculator'},
    {text:"Hold firm because re-negotiating sets a precedent I do not want.",map:'dominator'},
    {text:"Look for ways to help them because the ongoing partnership matters.",map:'yielder'},
]},

{ type:'style', text:"When I discover the other side has been dishonest about something important, I tend to:", options:[
    {text:"Pause and reassess the risks before responding.",map:'calculator'},
    {text:"Raise it with them directly and state my expectations.",map:'dominator'},
    {text:"Contain the issue so it does not derail the deal.",map:'yielder'},
    {text:"Raise it transparently and redirect toward honest terms.",map:'integrator'},
]},

{ type:'shadow', text:"When I am close to agreement and know the other side would accept less than what is currently on the table, I tend to:", options:[
    {text:"Lock in the current terms quickly before anything changes.",shadow:false},
    {text:"Hint that I have other options to see if they sweeten the deal even though I do not.",shadow:true},
    {text:"Close at the current terms because they are already good for me.",shadow:false},
    {text:"See how much further I can move them before they push back.",shadow:false},
]},

{ type:'style', text:"When the person across the table is clearly less experienced than me, I tend to:", options:[
    {text:"Follow the same process and preparation I would use with anyone.",map:'calculator'},
    {text:"Reduce my intensity because it does not feel right to take advantage of them.",map:'yielder'},
    {text:"Adjust my approach and explore whether there is value for both sides.",map:'integrator'},
    {text:"Stay focused on getting what I want regardless of their experience level.",map:'dominator'},
]},

{ type:'style', text:"When the other side introduces a new demand late in the negotiation, I tend to:", options:[
    {text:"Challenge it and push back firmly.",map:'dominator'},
    {text:"Explore what is behind the new demand before reacting.",map:'integrator'},
    {text:"Adjust something on my side to keep the deal going.",map:'yielder'},
    {text:"Pause and evaluate how it affects the overall deal.",map:'calculator'},
]},

{ type:'shadow', text:"When I secure a great deal but the other side looks disappointed, I tend to:", options:[
    {text:"Review my notes to check whether the outcome was actually fair.",shadow:false},
    {text:"Think, it is not my problem because a deal is a deal.",shadow:false},
    {text:"Reassure them they got a great deal too even though I know they did not.",shadow:true},
    {text:"Feel genuinely bad and wonder if I pushed too hard.",shadow:false},
]},

{ type:'style', text:"After closing a negotiation, I tend to think:", options:[
    {text:"I hope we're still on good terms.",map:'yielder'},
    {text:"What specifically did I do well that I can repeat next time?",map:'calculator'},
    {text:"Could I have got more?",map:'dominator'},
    {text:"Will this agreement hold up for both of us?",map:'integrator'},
]},
];
const stylePrimary = {
  dominator: "You lead with power and assertiveness. In any negotiation, your first instinct is to establish control, set the terms, and drive toward the outcome you want. You are direct, decisive, and completely unafraid of confrontation. Where others see conflict as something to avoid, you see it as the fastest route to clarity.\n\nDominators are the closers of the negotiation world. When a deal needs to get done, when someone needs to make a tough call, when the room has stalled and everyone is dancing around the issue, you are the one who cuts through. You say what others are thinking. You push when others hesitate. You never leave value on the table simply because you feel too polite to ask for it.\n\nYour competitive nature delivers strong results in the short term, but it can blind you to relational dynamics that matter over time. Not every negotiation is a single encounter. Many are rounds in a relationship that spans years or even decades. The Dominator's greatest asset is courage and conviction. The Dominator's greatest risk is isolation, because pushing too hard and too often means people eventually stop wanting to sit across from you.",
  integrator: "You lead with strategy and collaboration. Your natural instinct is to understand the full landscape before making your move, asking what each side truly needs, where there is overlap, and how the deal can be structured so everyone walks away with something meaningful. You are not naive about competition, but you believe the best outcomes come from creative problem solving rather than brute force.\n\nIntegrators are the deal architects of the negotiation world. Where Dominators fight over a fixed pie, you are asking whether the pie can be made bigger. You look for creative trades, package deals, and solutions that address underlying interests rather than surface positions. This approach consistently produces better total outcomes for all parties involved.\n\nYour collaborative nature is genuine, not performative. You actually care whether the other side gets a fair deal, not because you are soft, but because you understand that people who feel respected come back, refer others, and implement agreements willingly. The Integrator's greatest asset is vision and strategic depth. The Integrator's greatest risk is excessive optimism, because not everyone shares your values, and some will exploit your openness without hesitation.",
  yielder: "You lead with relationships and empathy. Your natural instinct in any negotiation is to build rapport, find common ground, and ensure everyone feels heard and respected. Conflict makes you uncomfortable, not because you cannot handle it, but because you genuinely believe better outcomes come from harmony than from fighting.\n\nYielders are the relationship builders of the negotiation world. You create environments where people feel safe enough to be honest about what they really need. This is a genuinely powerful skill. More information leads to better deals, and people share more with someone they trust. Your warmth is not weakness. It is a sophisticated negotiation tool that harder styles simply cannot replicate.\n\nHowever, your instinct to accommodate can work against you. When you give ground to maintain harmony, you are often sacrificing your own interests without realising it. The concessions feel small in the moment but they compound over time. The Yielder's greatest asset is trust and emotional intelligence. The Yielder's greatest risk is self sacrifice, because you are so focused on the other person's experience that you forget to advocate for your own needs.",
  calculator: "You lead with analysis and preparation. Before any negotiation, your instinct is to gather data, build models, map scenarios, and understand every variable at play. You trust evidence over intuition, process over personality, and logic over emotion.\n\nCalculators are the strategists of the negotiation world. You arrive more prepared than anyone else at the table. You have read the contract, checked the market rates, modelled the alternatives, and calculated your walk away point with precision. This preparation gives you a genuine edge, because you see things others miss, catch errors others overlook, and make decisions grounded in reality rather than wishful thinking.\n\nYour analytical nature, however, can create distance between you and the people across the table. While you are processing data, others are reading the room. While you are building spreadsheets, relationships are forming without you. The Calculator's greatest asset is preparation and rigour. The Calculator's greatest risk is detachment, because you can be so focused on the analysis that you forget to connect with the person sitting opposite you.",
};

const styleSecondary = {
  dominator: "Your Dominator secondary adds steel to your approach. When pushed, you do not fold. You push back. This competitive undercurrent means you are unlikely to be exploited, and you have the assertiveness to claim value when it matters most. Be careful though, because under pressure this energy can emerge more aggressively than you intend, undermining the trust your primary style has built. The key is deploying this steel deliberately rather than letting it erupt reactively when emotions take over.",
  integrator: "Your Integrator secondary adds strategic depth to your approach. You do not just react to what is in front of you. You look for creative solutions and consider the long term implications of every move. This collaborative undercurrent helps you find deals others miss and tempers any aggressive tendencies with genuine problem solving. The risk is occasional overthinking when decisiveness would serve you better. But when you get the balance right, this secondary influence makes your primary style significantly more effective.",
  yielder: "Your Yielder secondary adds warmth and emotional intelligence to your approach. You read people well, care about relationships beyond the transaction, and bring a human quality that puts others at ease. This softer undercurrent helps you build trust quickly and makes others comfortable being honest with you, which means you get better information than negotiators who lack this quality. The risk is that under pressure, this gentle side can cause you to concede when holding firm would produce a better outcome. Learning to manage this instinct is essential to your development.",
  calculator: "Your Calculator secondary adds analytical rigour to your approach. You back up your instincts with data, prepare thoroughly, and rarely walk into a negotiation without doing your homework first. This methodical undercurrent gives you confidence grounded in evidence rather than hope, and it helps you spot opportunities and risks that others miss entirely. The risk is retreating into analysis when the moment calls for decisive action or emotional connection. Using data as a shield rather than a tool is the pattern to watch for.",
};

const archetypes = {
'dominator-integrator': {
    name:'The Conqueror', emoji:'⚔️', tagline:'You fight hard and you fight smart.',
    narrative:"The Conqueror is one of the most formidable negotiation profiles. You combine the Dominator's competitive drive with the Integrator's strategic intelligence, meaning you do not just want to win. You want to win in a way that is sustainable and smart. You push hard for your interests, but you are perceptive enough to know when to pivot, when to concede a small point to gain a large one, and when to leave something on the table for the sake of a future deal. When you walk into a negotiation, the other side pays attention, and they prepare accordingly.",
    strengths:"Powerful combination of assertiveness and strategic thinking that makes you effective across a wide range of negotiation contexts. You push hard but know when to pivot, and people take you seriously from the moment you sit down at the table. Your competitive instinct means you rarely leave value unclaimed, while your strategic side ensures you do not burn bridges unnecessarily. You excel in complex, high stakes negotiations where both toughness and creativity are required. You can read the room, spot opportunities in the deal structure, and move decisively to capture them, which is a rare combination that few negotiators can match.",
    weaknesses:"Your competitive fire can overshadow your collaborative instincts at the table, even when collaboration would produce a better deal. You genuinely believe you are being fair, but your version of fair tends to be skewed in your favour more often than you realise. You set the terms, frame the options, and define what reasonable looks like, and while it is skilful negotiation, the other side can feel managed rather than partnered. Over time, counterparts may agree to your deals but resent the process that produced them. Your strength can also make it hard for you to recognise when a softer approach at the table would actually produce a superior outcome for everyone involved.",
    blindSpots:"You assume every counterpart respects strength and strategic thinking as much as you do, but that is simply not the case. Some people across the table shut down under pressure, meaning you miss information they would have shared willingly in a less competitive negotiation environment. You also underestimate how much your intensity affects counterparts emotionally, even when you believe you are being perfectly reasonable. You think you are being direct and efficient, but they may experience you as domineering or overwhelming. The deals you are proudest of may be the ones your counterparts are most bitter about, which creates long term risks to future negotiations that you cannot see from your side of the table.",
    howOthersSeeYou:"Impressive but intimidating at the table. People respect your competence and preparation, but they do not always enjoy negotiating with you. Less experienced counterparts find you overwhelming, while peers see you as a worthy adversary they need to prepare carefully for. Those who have negotiated with you before appreciate your reliability and directness, but newcomers often feel they are fighting uphill from the first offer. You command attention in any negotiation, and the real question is whether that attention comes with trust or with wariness.",
    underPressure:"When stressed, your Dominator side takes over and your strategic patience erodes quickly. The collaborative intelligence of the Integrator gives way to competitive aggression, and you may push harder on terms than the situation warrants. You become more focused on winning the point than finding the best deal, which can derail otherwise productive negotiations. The transition can be sudden, catching the other side off guard. One moment you are exploring options together, the next you are competing aggressively, and the other side feels ambushed by the shift in your approach.",
    watchOut:"Winning today's deal while losing tomorrow's relationship is the trap that Conquerors fall into most often. If counterparts remember being conquered, they come back armed next time, or they simply do not come back at all. Your track record of strong results may mask the relationships you are quietly eroding beneath the surface. Pay attention to whether people choose to negotiate with you, or whether they deal with you only because they have no alternative. The difference matters more than most Conquerors realise, and it determines whether your negotiation success is truly sustainable over time.",
    growthEdge:"Ask one more question before making your move in every negotiation you enter. Practice genuine curiosity by asking about the other side's constraints, pressures, and priorities before presenting your own position. You will be surprised how much more information flows when you lead with questions rather than demands. The best Conquerors win because people choose to deal with them, not because the other side had no choice. Your strategic brain can handle this shift, so let it guide you toward a more sustainable and ultimately more profitable approach to every deal you negotiate.",
    growthSteps:[
      'Before stating your position, ask about the other side\'s constraints and priorities',
      'Notice when you are pushing out of habit rather than strategy and pause',
      'After every negotiation, ask yourself whether the other side would choose to deal with you again',
    ],
  },
  'dominator-yielder': {
    name:'The Iron Chancellor', emoji:'🏛️', tagline:'Iron fist, velvet glove.',
    narrative:"The Iron Chancellor is a fascinating contradiction. Fierce competitiveness is wrapped in genuine warmth. You can drive a hard bargain while making the other person feel respected and heard. This is a rare and powerful combination because people trust you even while you are pushing them, and they often agree to terms they would reject from a pure Dominator. The tension between these two sides, however, means you are constantly navigating an internal tug of war that can make you unpredictable.",
    strengths:"Rare blend of toughness and warmth that catches people off guard and gives you a distinctive edge at the table. You drive hard bargains without making enemies, and your genuine empathy helps you read the room in ways that pure competitors simply cannot. People let their guard down around you because your warmth is real and not performed. This gives you access to information and goodwill that more aggressive negotiators never see. You can push hard on substance while maintaining the relationship, which is a skill most negotiators spend their entire careers trying and failing to master.",
    weaknesses:"Internal tug of war between winning and being liked makes you inconsistent in ways that undermine your effectiveness. In one meeting you are tough and direct, in the next you are accommodating and flexible, and neither you nor your counterpart can predict which version will show up. This unpredictability can breed uncertainty rather than trust over time. People do not know which Chancellor they are getting, and that ambiguity works against you more often than you realise. Building consistency into your approach would significantly amplify your natural talents.",
    blindSpots:"You switch between Dominator and Yielder modes based on emotion rather than strategy, and this pattern is more visible to others than you think. When you like someone, the Yielder takes over and you concede too much without realising it. When you feel disrespected, the Dominator emerges with disproportionate force that can damage relationships quickly. You are not choosing your approach deliberately. Your feelings are choosing for you, and that is a vulnerability that experienced negotiators will spot and exploit systematically over time.",
    howOthersSeeYou:"Charming and capable, but sometimes confusing to those who deal with you regularly. People enjoy your company but are not always sure where they stand with you at any given moment. Your warmth makes them feel safe, but your competitive moments can feel like betrayals of that warmth. Those who know you well learn to read which mode you are in and adjust accordingly. Those who do not may feel manipulated even when you are being entirely genuine in your intentions.",
    underPressure:"Under pressure, one of two things happens, and neither is strategic. Either your Dominator side takes over and you become aggressively competitive, losing the relational goodwill you have carefully built, or your Yielder side dominates and you accommodate simply to make the discomfort stop. The shift is driven by adrenaline rather than analysis, which means you are at your least effective precisely when the stakes are highest. Your task under pressure is to pause and consciously choose which energy the situation actually requires. Recognising the trigger before it takes hold is the critical skill you need to develop.",
    watchOut:"The perception of manipulation is your greatest reputational risk, even when your intentions are entirely honest. Because you are warm and tough in alternation, some people will conclude you are being strategically nice to soften them up for the kill. Even if that is not your intention, the pattern can look exactly that way from the outside. Once someone decides you are manipulative, every warm gesture becomes suspect and every tough moment confirms their theory. Consistency is your antidote, and it matters more than charm ever will.",
    growthEdge:"Choose your mode intentionally before each negotiation rather than letting the moment decide for you. Ask yourself whether this situation needs your Dominator energy or your Yielder warmth, then commit to that approach and stay there throughout. Consistency builds more trust than charm ever will, and deliberate flexibility is far more powerful than reactive switching between extremes. Your dual nature is genuinely a gift, but only when you control it instead of it controlling you. Start practising this deliberate choice in low stakes situations first, and build the habit before the pressure rises.",
      growthSteps:[
      'Before each negotiation, decide deliberately whether to lead with warmth or firmness',
      'Commit to that approach for the entire conversation rather than switching based on emotion',
      'Practice this deliberate choice in low stakes situations before testing it under pressure',
    ],
  },
  'dominator-calculator': {
    name:'The Inquisitor', emoji:'🔍', tagline:'Prepared, precise, and relentless.',
    narrative:"The Inquisitor combines competitive drive with analytical depth. You do not just want to win. You want to win on the evidence. You arrive at every negotiation armed with data, precedents, and carefully constructed arguments. Your approach is systematic and thorough, and you have a gift for finding the weakness in any position. People who come unprepared to negotiate with you are in for a very uncomfortable experience. Those who come prepared will find a worthy and formidable counterpart.",
    strengths:"Analytical rigour meets competitive drive, making you exceptionally well prepared and extremely hard to outmanoeuvre at the table. You arrive armed with data, spot weaknesses others miss, and build arguments that are genuinely difficult to counter. Your preparation gives you real confidence, not bluster, but the quiet certainty that comes from knowing your position is sound. In complex, technical, or high value negotiations you are truly formidable. When the stakes are high and the details matter, there is nobody you would rather have on your side of the table.",
    weaknesses:"You can come across as cold, clinical, and interrogating, which creates defensiveness in the people sitting opposite you. Your focus on data and logic means you often dismiss emotional or relational factors as noise, but those factors drive decisions just as much as facts do. People feel cross examined rather than engaged in a genuine conversation, and this makes them guarded and resistant. Guarded people hide information, dig into positions, and fight harder, which is the opposite of what you need for a good outcome. Your brilliance at analysis can actually make the negotiation harder when it is not paired with interpersonal skill.",
    blindSpots:"You believe that being right is enough, but it is not, and this blind spot costs you more than you realise. You undervalue the emotional dimension of negotiation, specifically how people feel about you, about the process, and about the outcome. A deal that is objectively fair but feels adversarial will face implementation problems, renegotiation attempts, and relationship damage. Data tells you what to do, but emotion tells you whether it will actually work in practice. Ignoring half the equation does not make you rational. It makes you incomplete as a negotiator.",
    howOthersSeeYou:"Respected but feared by those who have sat across from you before. People acknowledge your competence and preparation openly, but they do not look forward to sitting opposite you. You ask uncomfortable questions, challenge assumptions relentlessly, and rarely let anything slide past you. Colleagues value having you on their side of the table, while opponents wish you were on theirs instead. But few would describe the experience of negotiating with you as pleasant, and some will avoid it entirely if they can find an alternative.",
    underPressure:"Under pressure, you retreat further into analysis and become more rigid in your positions. You want more data, more time, and more certainty before committing to anything, and when you cannot get those things you become adversarial. Your Dominator side asserts itself with sharp, clinical attacks on the other side's position that feel personal even when they are not intended that way. You win the argument but lose the room in the process. The combination of increased aggression and decreased warmth under pressure is your most dangerous and most predictable pattern.",
    watchOut:"Being right does not mean people will agree with you, and this is a lesson that many Inquisitors learn too late. If the other party feels disrespected by your clinical approach, they may walk away from a good deal just to spite you. Your thoroughness can also create a power imbalance that makes less prepared counterparts feel humiliated rather than persuaded. Victory through humiliation creates enemies, not partners, and enemies have very long memories. The resistance you encounter is often a reaction to your style rather than your substance.",
    growthEdge:"Add warmth to your precision and watch your effectiveness multiply. A single genuine question about the other person, their challenges, their pressures, what keeps them up at night, can unlock more value than an hour of data analysis. Try opening your next negotiation with curiosity about the person before diving into the problem at hand. Your analytical skills are already world class and do not need more sharpening. Your relational skills are where the real growth lies, and even small improvements there will dramatically increase your overall effectiveness.",
      growthSteps:[
      'Open your next negotiation with a genuine question about the person before discussing the problem',
      'Watch how the conversation changes when you lead with curiosity rather than data',
      'Ask yourself after each meeting whether the other side felt respected, not just logically persuaded',
    ],
  },
'integrator-dominator': {
    name:'The King', emoji:'👑', tagline:'You lead with vision and backbone.',
    narrative:"The King is the most commanding presence at any negotiation table. You combine the Integrator's collaborative vision with the Dominator's competitive backbone, meaning you genuinely seek outcomes that work for everyone, but you are absolutely willing to fight for your position when necessary. People defer to your proposals because you project both fairness and strength in equal measure. You set the tone, frame the agenda, and guide the conversation with a confidence that feels earned rather than imposed. When you walk into a negotiation, the other side adjusts their approach to match yours.",
    strengths:"Natural authority combined with genuine collaborative intent makes you one of the most effective negotiation profiles in any setting. You seek fair outcomes but are not afraid to fight for what matters, which earns deep respect from counterparts on both sides of the table. Your combination of vision and steel means you can hold a room, manage multiple stakeholders, and drive toward deals that are both ambitious and achievable. People trust your proposals because they serve the collective interest, not just your own. In multi party negotiations and complex deal structures, you are the one everyone looks to for direction and resolution. You consistently close deals that others cannot because you combine the creativity to find solutions with the backbone to push them through.",
    weaknesses:"Your confidence in your own proposals can tip into rigidity more easily than you expect, particularly when you are under time pressure. When your ideas are challenged at the table, your Dominator side may emerge more aggressively than you intended, and your certainty that you have found the best deal structure, because you have considered everyone's interests, can make you dismissive of alternatives you have already evaluated and rejected. You negotiate well, but you do not always listen well, and there is a fine line between guiding a negotiation toward a good outcome and controlling the conversation so tightly that the other side feels managed rather than partnered. That line is thinner than you think, and others see you cross it more often than you realise.",
    blindSpots:"You believe your proposals are best because you have considered everyone's interests, but the question is whether you have really listened to what the other side needs or just assumed you already know. Your confident, decisive style can crowd out quieter counterparts and unconventional ideas that might actually improve the deal. The people most likely to offer you information you do not already have are the ones least likely to speak up when you are dominating the conversation. You may not even notice their silence, which means you are negotiating with incomplete information while believing you have the full picture. This gap between your perception and reality is your most significant vulnerability at the table.",
    howOthersSeeYou:"Commanding and fair, but sometimes overbearing in ways you do not intend. People genuinely respect your proposals and often defer to your framing of the deal, sometimes too readily for the good of the outcome. But some counterparts experience your approach as steamrolling dressed up as collaboration, and they resent it quietly. They agree to your terms because challenging you feels futile, not because they are genuinely persuaded the deal is fair. The agreement you see on the surface may be thinner than you think, and it may not survive the pressures of implementation when the other side reflects on how little input they actually had.",
    underPressure:"Under pressure, the Dominator emerges and your collaborative patience erodes quickly. Your strategic thinking gives way to competitive instinct, and you begin making unilateral demands, issuing ultimatums, and pushing terms through rather than building agreement. The transition can be sudden and jarring for counterparts who trusted your collaborative approach and expected it to hold. They feel blindsided by the shift, and the trust you have built across the table can evaporate in a single heated exchange. Learning to recognise the early warning signs of this pattern is essential to maintaining your effectiveness in tough negotiations.",
    watchOut:"The moment the other side feels you are dictating terms rather than collaborating on a deal, you lose the trust that makes you effective. Negotiators who stop listening to counterparts eventually become adversaries, and the difference between the two is often invisible to the person doing the talking. Watch for signs that the other side is agreeing to your proposals to end the conversation rather than because they genuinely support the terms. Compliance at the table is not commitment to the deal, and the gap between the two will show up during implementation. The strongest negotiators actively invite pushback because they understand that challenge makes deals better and more durable.",
    growthEdge:"Practice genuine curiosity by asking questions you do not already know the answer to at the negotiation table, and sit with the answers before responding with your own proposal. Invite pushback explicitly, not as a token gesture, but because the best deals are built when both sides genuinely contribute to the structure. In your next negotiation, let the other side propose the first solution and resist the urge to improve it immediately. Your negotiation outcomes are strongest when the other side feels they chose the deal, not when they feel they had no alternative but to accept yours.",
    growthSteps:[
      'In your next negotiation, let the other side propose the first solution before presenting your own',
      'When your proposal is challenged, ask a question before defending your position',
      'After every deal, ask yourself whether the other side would describe the process as collaborative or controlled',
    ],
  },
'integrator-yielder': {
    name:'The Ambassador', emoji:'🤝', tagline:'Trusted by everyone at the table.',
    narrative:"The Ambassador is the most trusted profile in the negotiation world. You combine the Integrator's strategic collaboration with the Yielder's genuine warmth, creating an approach that makes counterparts feel safe, heard, and respected at the table. Others open up to you naturally, sharing information and concerns they would hide from more competitive negotiators. You find creative deal structures because people tell you what they really need, not just what they are willing to say in a formal negotiation.",
    strengths:"Exceptional at building trust and finding deal structures everyone feels good about, which gives you a distinctive advantage in relationship heavy negotiations. Your warmth is genuine, your strategic thinking is sharp, and the combination means you consistently find deals that others miss entirely. Counterparts actively want to negotiate with you because they believe the process will be fair. They implement agreements willingly because they feel ownership over the outcome, and they come back for future deals with confidence. In long term, relationship heavy negotiations where repeat business matters, you are simply unmatched at the table.",
    weaknesses:"You avoid necessary confrontation at the table, even when pushing harder would serve everyone's interests better in the long run. When the deal requires you to hold a firm position, draw a clear line, or walk away, you hesitate because the potential damage to the relationship feels too costly. The thought of creating tension at the table makes you genuinely uncomfortable, and this discomfort leads to concessions that serve the relationship at the expense of the deal. Over time, you leave significant value on the table, not because you lack intelligence, but because you lack the willingness to create the temporary tension that better deals require. Recognising this pattern is the first step toward changing it.",
    blindSpots:"You assume good faith too readily and extend trust to counterparts faster than the evidence warrants. Not everyone across the table shares your collaborative values, and some negotiators will exploit your openness, use your trust against you, and take advantage of your reluctance to confront dishonesty in a deal. Your warm, open style is a magnet for manipulation by those who see kindness as weakness at the negotiation table. Your instinct to see the best in every counterpart can delay your recognition of bad faith tactics until significant damage to the deal has already been done. Trust should be earned incrementally and verified regularly, not given freely on the assumption that the other side operates as you do.",
    howOthersSeeYou:"Warm, trustworthy, and genuinely liked by almost everyone who negotiates with you. Counterparts enjoy the process and often feel the outcomes are fair, which is a real and valuable asset. However, tougher negotiators may view you as a soft target they can push without consequence at the table. They appreciate your nature but exploit it when it serves their deal interests. Your own stakeholders may wish you came back with stronger terms, even if they admire the relationships you build with counterparts along the way.",
    underPressure:"Under pressure at the table, your Yielder side dominates completely and your strategic thinking goes offline. You become conflict averse, accommodating, and focused on preserving the relationship at any cost, even when that cost means accepting deal terms that are unreasonable. You make concessions you will regret once the pressure subsides and you can evaluate the deal clearly again. You need preset boundaries, meaning lines drawn before you walk into the negotiation, because in the moment your instinct is always to give ground. Writing down your walk away point before every negotiation is not optional for you. It is essential.",
    watchOut:"Dominators and manipulators read your warmth as weakness and will test you at the table accordingly. Without steel underneath your diplomacy, you will consistently give away more value than you should and more than your stakeholders expect from the deal. The hardest lesson for Ambassadors is that firmness and warmth are not opposites at the negotiation table. You can say no with a smile, hold your position with empathy, and protect your deal interests without damaging the relationship. Most counterpart relationships are far more resilient than you assume, and they can handle the occasional firm boundary in a negotiation.",
    growthEdge:"Practice saying no at the table without apologising, starting in low stakes negotiations and building from there. Hold a position longer than feels comfortable and observe what happens to the deal. You will usually find that counterparts respect the boundary and the relationship survives perfectly well. Firm boundaries delivered with warmth is your ultimate negotiation superpower, but you need to actually deploy it rather than just knowing it exists. Before every concession, ask yourself this: am I giving this because it is strategic for the deal, or because I am uncomfortable with the tension at the table?",
    growthSteps:[
      'Before every concession, ask yourself whether you are giving it because it is strategic or because you are uncomfortable',
      'Practice saying no without apologising, starting in low stakes negotiations',
      'Write down your walk away point before walking in and hold at least one position firm',
    ],
  },
'integrator-calculator': {
    name:'The Vizier', emoji:'🎯', tagline:'Three moves ahead of everyone.',
    narrative:"The Vizier is the master negotiation strategist. You combine the Integrator's collaborative problem solving with the Calculator's analytical depth, creating an approach that sees further into the deal structure and thinks deeper about the terms than any other profile. You do not just prepare for the negotiation. You prepare for every possible direction it could take. You anticipate counterpart moves, map deal scenarios, and build proposals that are creative, robust, and genuinely hard to fault. You are the person everyone wants advising them before they walk into a tough negotiation.",
    strengths:"Strategic mastermind who sees the full deal picture and plans several moves ahead of everyone else at the table. You build proposals that are both creative and bulletproof, combining genuine collaboration with rigorous analysis in a way that few other negotiation profiles can match. Your proposals are consistently better thought through than anyone else's, backed by both logic and a genuine understanding of all parties' deal interests. In complex, multi party negotiations with many moving parts, you are genuinely invaluable at the table. The deals you build tend to last because they are designed to withstand changing circumstances and implementation pressures over time.",
    weaknesses:"You can overthink deal structures and under act at the table, and this pattern costs you more negotiation outcomes than you realise. While you are planning the perfect proposal, opportunities pass and faster moving counterparts claim the value you were still analysing. Your need for thoroughness creates delays that frustrate the other side, and your reluctance to commit to terms until you have analysed every angle can look like indecisiveness or lack of conviction at the table. Sometimes a good deal closed today beats a perfect deal structure next week, and you struggle deeply with that truth. Learning to commit to terms at eighty percent certainty rather than waiting for ninety five is your most important development area as a negotiator.",
    blindSpots:"You trust your preparation process more than your instincts at the table, and sometimes the right move in a negotiation is obvious and does not need a spreadsheet to justify it. Your analytical nature can also make you dismissive of gut feelings, both yours and your counterpart's, which means you miss signals at the table that cannot be quantified but are nonetheless critically important to the deal. Not everything that matters in a negotiation can be measured, and not every deal term improves with more analysis. The counterparts sitting across from you sometimes know things intuitively about the deal that your models cannot capture. Learning to value and integrate that intuitive information alongside your analytical data would make you significantly more effective at the table.",
    howOthersSeeYou:"Brilliant but slow at the table, which creates a perception gap that varies dramatically depending on who you are negotiating with. Counterparts respect your intellect and the quality of your proposals, but they often get frustrated waiting for them. Fast moving negotiators feel held back by your process and may disengage or close with someone else before you are ready to present your carefully crafted deal structure. Detail oriented counterparts love negotiating with you because they appreciate your rigour. The perception gap between those who value thoroughness and those who value speed at the table often determines how effective you are in any given negotiation.",
    underPressure:"Under pressure at the table, you retreat into analysis and demand more time, more data, and more certainty before committing to any terms. If pushed beyond your comfort zone by aggressive counterparts, you become rigid about process, insisting on proper review when the negotiation demands flexibility and speed. Your Integrator side wants to find a creative deal structure. Your Calculator side will not let you commit until you have verified it from every angle. The result is paralysis at the worst possible moment in the negotiation, precisely when decisive action is most needed to capture value on the table.",
    watchOut:"Analysis paralysis is a real and present danger for you at the table, and competitive negotiators know how to use your need for information against you by creating artificial time pressure on the deal. They know that if they rush you, you will either make a worse decision than usual or freeze entirely, and both outcomes serve their deal interests. Have a framework ready for rapid decisions at the table, not perfect decisions, but good enough decisions you can make under pressure without abandoning your values. Preparation for speed is just as important as preparation for substance in any negotiation.",
    growthEdge:"Set a decision deadline for yourself before every negotiation and honour it regardless of whether you feel completely ready to commit to terms. Practice making deal decisions at eighty percent certainty instead of waiting for ninety five, and observe how the outcomes compare. The value of a timely decision at the table almost always exceeds the value of a perfect one that arrives too late. Your analytical and collaborative negotiation skills are already world class. Your speed of execution at the table is where the real growth lies, and it is the one thing holding you back from being truly exceptional as a negotiator.",
    growthSteps:[
      'Set a decision deadline before every negotiation and honour it regardless of whether you feel completely ready',
      'Practice committing to deal terms at eighty percent certainty and observe how the outcomes compare',
      'When you want more data, ask yourself whether the additional analysis would actually change the terms you offer',
    ],
  },
  'yielder-dominator': {
    name:'The Shield Bearer', emoji:'🛡️', tagline:'Gentle until provoked, then formidable.',
    narrative:"The Shield Bearer is the negotiation world's dark horse. You lead with warmth, empathy, and a genuine desire for harmony, but underneath that gentle exterior lies real steel. When pushed too far, when your values are violated, or when someone you care about is threatened, you transform. The shift catches people off guard because they have been negotiating with the Yielder and suddenly find themselves facing the Dominator. It is effective once, but the question is whether you can access that strength before you have already given away too much.",
    strengths:"Deceptively resilient in ways that consistently surprise the people sitting opposite you. You lead with warmth but dig in hard when it matters, and people underestimate you at their peril. Your initial gentleness creates trust and openness, allowing you to gather information and build rapport before the negotiation gets truly tough. When it does get tough, you have reserves of assertiveness that surprise counterparts who mistook your kindness for weakness. You are also fiercely protective of your team and your values, which makes you a powerful advocate when the cause matters to you.",
    weaknesses:"Your default is accommodation, and it takes significant provocation to activate your tougher side, which means you often give ground unnecessarily long before you ever consider pushing back. The threshold for switching from Yielder to Dominator is set far too high for your own good. By the time you assert yourself, you have already conceded important points that are difficult or impossible to recover. Your strength exists, but it activates too late and too inconsistently to be reliably useful. Learning to lower that threshold is the single most important thing you can do to improve your negotiation outcomes.",
    blindSpots:"You wait too long to assert yourself, and by the time you push back, you have already given away too much ground to recover your position. You also do not realise how jarring the switch is for others who have calibrated to your gentle style and feel blindsided when the Dominator appears. This inconsistency can actually damage trust more than being firm from the start would have. People prefer predictable toughness to unpredictable eruptions, even if the eruption is justified. Your pattern of patience followed by a sudden hard stance creates confusion rather than respect.",
    howOthersSeeYou:"Initially warm and easy to work with, the kind of person people genuinely enjoy negotiating with and look forward to seeing. But those who have triggered your Dominator side remember it vividly and tell others about the experience. The contrast between your normal gentleness and your provoked toughness can feel like a betrayal of character, even though both sides are genuinely you. Over time, people who know you learn to respect your boundaries and avoid crossing them. But newcomers will always test those limits because your default warmth gives no indication of the steel beneath.",
    underPressure:"Under pressure, you oscillate between over accommodation and disproportionate pushback in a pattern that undermines both your relationships and your results. You absorb, absorb, and absorb some more, and then you snap. The snap is usually bigger than the situation requires because you are responding to accumulated frustration, not just the current provocation. This pattern is your most important one to recognise and manage, because it costs you credibility and trust every time it occurs. Learning to release pressure incrementally rather than in a single burst is the key to transforming this pattern.",
    watchOut:"Experienced negotiators test limits early, and if you fold on the first three requests, they know you will fold on the fourth and the fifth as well. Your pattern of accommodation followed by sudden resistance creates an exploitable predictability that skilled counterparts will recognise and use against you. They push until you snap, then back off just enough to keep you in Yielder mode for the next round of requests. Dominators in particular will read you quickly and systematically work your pattern to extract maximum concessions. Recognising this dynamic from outside yourself is the first step toward breaking free of it.",
    growthEdge:"Set your non negotiables before you walk in and write them down where you can see them during the conversation. Know your lines in advance so you do not have to find them under pressure when your instincts are pulling you toward accommodation. Practice early, small assertions by pushing back on the first issue rather than waiting until the tenth when your frustration is already building. You have both the warmth and the steel inside you. Your task is integrating them into a consistent approach rather than alternating between extremes that confuse everyone, including yourself.",
    growthSteps:[
      'Write your three non-negotiables down before every negotiation where you can see them',
      'Push back on the first issue rather than waiting until frustration builds',
      'Build your assertiveness muscle by pushing back on small everyday requests before testing it in high stakes situations',
    ],
  },
  'yielder-integrator': {
    name:'The Emissary', emoji:'📜', tagline:'You build bridges others cannot.',
    narrative:"The Emissary is the natural peacemaker and bridge builder. You combine the Yielder's warmth and empathy with the Integrator's strategic collaboration, creating an approach that excels at finding common ground even in the most difficult situations. People trust you instinctively, and you use that trust to build solutions that genuinely serve everyone involved. You are at your best in relationship heavy, long term, or emotionally complex negotiations where other styles struggle and founder.",
    strengths:"Genuine relationship builder with strategic awareness that allows you to turn goodwill into smart, lasting agreements. You create trust naturally, find common ground effortlessly, and have the strategic thinking to convert that goodwill into deals that actually work for everyone involved. People open up to you, share their real concerns, and work with you to find solutions because they believe you genuinely care about their interests. And you do, which is precisely why it works so consistently. You see opportunities for mutual gain that more adversarial styles miss entirely, and you bring a calming presence that helps everyone at the table think more clearly.",
    weaknesses:"You prioritise harmony over outcomes and may agree to terms that feel good in the moment but cost you significantly in the long run. Your desire for positive relationships means you are reluctant to have difficult conversations, push for better terms, or walk away from a deal that is not good enough. The result is a pattern of agreements you feel warm about but that your stakeholders wish were stronger. Over time, this pattern erodes your credibility with the people you represent, even as it strengthens your relationships with the people across the table. Recognising the gap between feeling good about a deal and actually achieving a good deal is essential to your growth.",
    blindSpots:"You confuse compromise with collaboration more often than you realise, and the distinction matters enormously. Splitting the difference feels fair but often means neither side gets what they really need. True collaboration means finding creative solutions that serve both sides, and sometimes that requires uncomfortable conversations about priorities, trade offs, and what you are genuinely not willing to give up. You tend to skip those conversations in favour of quick, comfortable middle ground that looks fair but actually leaves significant value unclaimed. Building the discipline to stay in discomfort longer would dramatically improve your outcomes.",
    howOthersSeeYou:"Lovely to deal with, but sometimes too accommodating for your own good and for the good of those you represent. People genuinely like you and feel respected by you, which is a real and valuable asset in any negotiation context. But tougher counterparts may see you as someone they can push without fear of consequence. Your own stakeholders may wish you came back with stronger results, even though they appreciate your relationships. The warmth that makes you effective in building relationships can make you predictable and therefore exploitable when it comes to protecting your interests.",
    underPressure:"Under pressure, your Yielder side dominates completely and your strategic Integrator thinking goes offline just when you need it most. You become focused on ending the discomfort as quickly as possible, which usually means making concessions that solve the immediate tension but create long term problems. You agree to things you will question later when you have the space to think clearly again. Having clear walk away criteria written down before the negotiation is essential for you because it keeps your strategic brain engaged when your emotional brain wants to please. Without those written boundaries, you are negotiating against yourself.",
    watchOut:"Your likability is a genuine asset, but it is not a strategy by itself, and confusing the two is dangerous. Tough negotiators will enjoy your company while they systematically claim everything on the table without you noticing until it is too late. Being liked does not protect your interests. Being clear about your needs does, even when clarity creates temporary discomfort. Make sure every act of warmth is paired with clarity about what you need from the deal, because warmth without boundaries is an open invitation for exploitation.",
    growthEdge:"Before every concession, ask yourself this question: am I giving this because it is strategic, or because I am uncomfortable with the tension? If the answer is discomfort, hold your ground and observe what happens. Practice sitting with silence after making a request instead of filling the gap with a softer version of what you just asked for. Your warmth will survive one firm moment, and the relationship is almost certainly more resilient than you fear. Build the habit of pausing before conceding, because most of the time you will discover that the tension passes entirely on its own.",
    growthSteps:[
      'Before giving a concession, pause and count to five before responding',
      'Ask yourself whether the tension you feel is real danger or just discomfort',
      'Hold your position on one point longer than feels comfortable and observe what happens',
    ],
  },
  'yielder-calculator': {
    name:'The Counsel', emoji:'📖', tagline:'The quiet voice everyone should listen to.',
    narrative:"The Counsel is the most insightful profile in the negotiation world, and also the most underestimated. You combine the Yielder's empathy and people reading ability with the Calculator's analytical depth, meaning you understand both the numbers and the humans behind them. You see patterns others miss, anticipate problems before they materialise, and have the emotional intelligence to navigate complex interpersonal dynamics with grace. Your only problem is that you rarely assert any of this forcefully enough to shape the outcome in your favour.",
    strengths:"Thoughtful, perceptive, and deeply knowledgeable in ways that consistently surprise people when they take the time to listen. You notice things others miss entirely, from emotional undercurrents to logical inconsistencies to hidden opportunities buried in complexity. Your combination of empathy and analysis gives you a uniquely complete picture of any negotiation that few other profiles can match. People who listen to you make better decisions, and you bring a calming, grounding presence to tense situations that helps everyone think more clearly. Your insight is genuinely rare and genuinely valuable whenever it reaches the table.",
    weaknesses:"Extremely risk averse and conflict avoidant in ways that significantly limit your impact at the negotiation table. You would rather observe than engage, sitting on insights that could change the entire direction of the deal because sharing them might create confrontation or put you in the spotlight. Your analysis is excellent but your execution is weak, and the gap between the two is where your potential goes unrealised. You prepare beautifully and then underperform at the table because asserting yourself feels deeply uncomfortable. The world only benefits from your insight when you actually share it.",
    blindSpots:"You over prepare and under execute, and you have convinced yourself this is wisdom rather than avoidance, but it is not. You tell yourself you are being thoughtful when you are actually being timid, and the distinction matters enormously. The insight you are holding back, because it might be wrong, might cause conflict, or might draw unwanted attention to you, is usually the most important thing anyone in the room could say. Your silence has a cost, and you are not accounting for it in your analysis. Every time you hold back, the negotiation proceeds without your best contribution, and everyone at the table loses as a result.",
    howOthersSeeYou:"Quiet, knowledgeable, and pleasant, but often invisible in exactly the moments when your insight would be most valuable. People appreciate your contributions when you make them but forget to seek your input because you do not demand it or signal its availability. More assertive styles talk over you, assume your silence means agreement, and move forward without the benefit of your analysis. This is not entirely their failure. You need to make your value visible rather than waiting for others to discover it, because most of them are too busy with their own concerns to go looking.",
    underPressure:"Under pressure, you withdraw psychologically even when you remain physically present in the room. You become quieter, more analytical, and less engaged with the people around you, retreating into your notes, your data, and your preparation, all the things that feel safe. Meanwhile, the negotiation moves forward without you, and others make the decisions you should be influencing. Your absence from the conversation at critical moments is your biggest vulnerability. The higher the stakes, the more you retreat, which is exactly the opposite of what the situation requires.",
    watchOut:"Knowledge without assertiveness is wasted potential, and every other style at the table will outpace you if you do not speak up. Dominators will dominate the conversation, Integrators will lead the process, Yielders will at least try to build rapport and influence the tone, and you will sit there with the best analysis in the room watching everyone else make suboptimal decisions because you did not contribute. Your insight only has value when it reaches the table. Keeping it inside your head protects you from discomfort but deprives everyone else of your genuine contribution. The cost of your silence is borne by everyone, not just you.",
    growthEdge:"Share your insights out loud, every single time, not just when asked directly. Make a commitment to speak up at least fifty percent more than feels comfortable in your next negotiation and observe what happens to the quality of the discussion. Prepare not just your analysis but your talking points, writing down what you want to say and when you want to say it during the conversation. Your analysis is a powerful tool, but only if you use it actively rather than keeping it in reserve. Start treating your voice as part of your preparation rather than an optional addition that depends on whether someone invites you to speak.",
    growthSteps:[
      'Prepare one specific insight you will share out loud in your next negotiation',
      'Write down not just your analysis but the exact words you will use to present it',
      'Speak up at least once before being asked and notice how people respond',
    ],
  },
  'calculator-dominator': {
    name:'The Spymaster', emoji:'🗡️', tagline:'Patient, precise, and lethal.',
    narrative:"The Spymaster is the most dangerous profile at the table, not because you are aggressive, but because you are calculated. You combine the Calculator's analytical patience with the Dominator's competitive drive, creating an approach that gathers intelligence, waits for the right moment, and strikes with surgical precision. You rarely make the first move, but when you do move, it is decisive and perfectly timed. People who underestimate your patience do so at their peril.",
    strengths:"Lethal combination of patience and competitiveness that makes you exceptionally effective in high stakes, complex negotiations with sophisticated counterparts. You gather information others overlook, wait for openings others miss, and execute with a precision others simply cannot match. Your analytical preparation means you understand the terrain better than anyone else at the table, and your competitive drive means you exploit that understanding fully when the moment arrives. You are the chess player at a table of checkers players, thinking several moves ahead while others react to what is immediately in front of them. In negotiations where information is power and timing is everything, you are in your element.",
    weaknesses:"Perceived as cold, calculating, and untrustworthy by those who sense your approach even when they cannot quite articulate what makes them uneasy. Your patience looks like plotting, your analysis looks like surveillance, and your precision looks like manipulation, even when your intentions are entirely legitimate. People who feel studied do not open up, which creates a painful irony at the heart of your style. The more deliberately you try to gather intelligence, the less people are willing to share with you freely. Your very method of building advantage can undermine the information flow you depend on.",
    blindSpots:"You wait so long for the perfect moment that you miss good ones, and this pattern of perfectionism disguised as patience costs you more than you realise. Not every negotiation requires a masterplan, and not every counterpart is an adversary to be outmanoeuvred rather than a partner to collaborate with. Your default assumption that information is power can make you hoard rather than share, creating a dynamic where both sides operate with incomplete pictures and reach worse outcomes than necessary. The irony is that your strategic intelligence is sophisticated enough to recognise this, but your competitive instinct overrides that recognition in the moment. Learning to distinguish between negotiations that require your full Spymaster approach and those that need something simpler would significantly improve your results.",
    howOthersSeeYou:"Impressive when people see you work, but unsettling in how you work, which creates a complicated reputation over time. Counterparts sense they are being studied even when they cannot articulate why, and this awareness makes them guarded and cautious. Once your approach is understood, and experienced negotiators will understand it quickly, people become more careful around you. They share less, position more carefully, and treat every interaction as potentially strategic. This guarded dynamic is the opposite of what produces truly good deals for both sides.",
    underPressure:"Under pressure, your Dominator side takes over with cold precision that can feel ruthless to those on the receiving end. You become sharply competitive, using your analytical advantage as a weapon rather than a tool for finding mutual benefit. Your moves become more aggressive, your patience thins dramatically, and your strikes become more pointed and personal. This pressurised version of you is effective in the short term but burns trust rapidly and creates adversaries who will remember the experience. The damage you do under pressure often takes far longer to repair than the advantage it creates.",
    watchOut:"When people figure out your approach, and experienced negotiators will figure it out, they stop sharing information entirely and your power base erodes. Your effectiveness depends on access to intelligence, and that access evaporates once you are identified as a calculating competitor who uses information strategically against others. The Spymaster's greatest vulnerability is exposure, because once people know what you are, they build walls where there used to be doors. The reputation you build follows you from one negotiation to the next, and in close industries, word travels fast. Your future effectiveness depends on how you manage your current reputation.",
    growthEdge:"Show more of your hand deliberately and observe how the dynamic changes. Selective transparency, meaning sharing some of your analysis, your reasoning, and your constraints openly, actually generates better intelligence than secrecy because people naturally reciprocate openness with openness. In your next negotiation, share one piece of genuine information early and watch how the conversation shifts. Trust, strategically deployed, is more powerful than secrecy, and you are strategic enough to deploy it brilliantly. The transition from information hoarder to selective sharer is the single most impactful change you can make to your negotiation approach.",
    growthSteps:[
      'Share one piece of genuine information early in your next negotiation',
      'Watch how the other side\'s openness changes in response to your transparency',
      'Ask yourself whether secrecy is truly serving you or just making others guard against you',
    ],
  },
  'calculator-integrator': {
    name:'The Architect', emoji:'🏗️', tagline:'You build deals that stand the test of time.',
    narrative:"The Architect is the master deal builder. You combine the Calculator's analytical rigour with the Integrator's collaborative creativity, producing agreements that are technically sound, strategically robust, and genuinely fair to all parties involved. Your deals do not just close. They last. They survive implementation challenges, changing circumstances, and the test of time. You may not be the fastest or the most charismatic negotiator in the room, but the structures you build consistently outlast everyone else's.",
    strengths:"Deep analysis combined with genuine collaboration produces agreements that everyone can live with over the long term, and that is rarer than most people realise. You understand both the technical details and the human dynamics, and you weave them together into proposals that are creative, fair, and genuinely robust. Your thoroughness means your deals have fewer surprises, fewer renegotiations, and fewer breakdowns during implementation. In complex, multi stakeholder negotiations with many moving parts, you are the most valuable person at the table. The agreements you produce tend to outlast those built by faster, more aggressive negotiators because your foundations are stronger.",
    weaknesses:"Slow and methodical to a fault, which creates friction with counterparts who operate at a faster pace. Fast moving negotiations and aggressive counterparts can leave you behind while you are still mapping variables and building your analysis. Your need for thoroughness creates delays that test others' patience and sometimes cost you opportunities that close before you are ready. Your desire to build the perfect deal can prevent you from closing a genuinely good one. The gap between your preparation timeline and others' decision speed is a consistent source of friction, frustration, and missed opportunities.",
    blindSpots:"You value elegance and completeness over speed, and you have built a comfortable narrative that careful analysis is always better than quick decisions, but that is not always true. Sometimes an imperfect deal done quickly beats a perfect deal that never gets closed because the window of opportunity passed while you were still refining your model. You also underestimate how much your deliberative style frustrates counterparts who operate on instinct and speed. They see your thoroughness as obstruction rather than value, and they may disengage from the process before you are ready to present your carefully crafted solution. Recognising when speed matters more than precision would make you significantly more effective.",
    howOthersSeeYou:"Thorough, fair, and sometimes maddeningly slow, which creates very different impressions depending on who you are working with. People who value quality and durability love working with you and seek you out for important negotiations. People who value speed and decisiveness find you frustrating and may avoid working with you on time sensitive matters. Your reputation for solid, lasting deals earns you genuine respect over the long term. But in the immediate moment, counterparts may feel stuck in a process that moves at your pace rather than theirs, and that perception can cost you opportunities.",
    underPressure:"Under pressure, you cling to process with an intensity that can feel obstructive to everyone else in the room. You insist on proper analysis, thorough review, and structured decision making when the situation may demand flexibility, improvisation, and speed. If your process is disrupted by time pressure, aggressive counterparts, or unexpected developments, you become rigid and anxious rather than adaptive and creative. The very structure that gives you strength in calm conditions becomes a cage under pressure. Building comfort with imperfect information and rapid decisions is your most critical development area.",
    watchOut:"Competitive negotiators exploit your need for structure by creating chaos, because disrupting your process is the fastest way to neutralise your advantage. If they can rush your timeline, introduce unexpected variables, or force you out of your prepared framework, they push you into reactive mode where you are at your weakest and most vulnerable. Always have a contingency approach ready for when your primary plan is disrupted, because it will be disrupted eventually. The best Architects design for earthquakes, not just fair weather. Prepare for the process breaking down just as thoroughly as you prepare for it working perfectly.",
    growthEdge:"Build a good enough threshold into every preparation before you walk in the door. Define clearly what eighty percent of your ideal outcome looks like, and decide in advance whether you would accept it if offered. This gives you a decision point that prevents endless optimisation and allows you to close deals while the opportunity is still available. Practice closing deals that are good rather than holding out for deals that are perfect, and observe how the outcomes compare over time. Your architecture skills are already exceptional. Now build a clock into your blueprints and watch your effectiveness increase dramatically.",
   growthSteps:[
      'Define clearly what eighty percent of your desired outcome looks like before every negotiation',
      'If offered that eighty percent, commit to accepting it rather than optimising further',
      'Practice closing deals that are good rather than holding out for deals that are perfect',
    ],
  },
  'calculator-yielder': {
    name:'The Oracle', emoji:'🔮', tagline:'You see everything. Now act on it.',
    narrative:"The Oracle has the deepest insight of any profile. You combine the Calculator's analytical power with the Yielder's emotional intelligence, giving you a complete understanding of both the data and the people involved. You see dynamics others miss, anticipate problems before they surface, and understand motivations at a level that borders on intuitive. Your challenge is not perception. It is action. You see what needs to happen but struggle to make it happen, because acting means risking conflict, exposure, and the discomfort of asserting yourself in front of others.",
    strengths:"Deeply empathetic and extraordinarily perceptive in ways that consistently reveal things others miss entirely. You understand people and situations at a level most negotiators never reach, regardless of how many years of experience they accumulate. Your combination of analytical and emotional intelligence means you see the complete picture, including the numbers, the relationships, the hidden agendas, and the unspoken concerns that drive real decisions. Your insight is genuinely rare, and when you do share it, it fundamentally changes how people think about the problem. You are the advisor everyone needs and few know to ask for, which is both your gift and your limitation.",
    weaknesses:"You observe and advise but struggle to lead, preferring to support someone else's negotiation rather than take the reins yourself. This means your exceptional insight often goes unused or gets filtered through less capable people who dilute or misunderstand it. You have all the information needed to be outstanding at the table, but you cannot bring yourself to sit at the head of it. This is not humility, despite how it might feel from the inside. It is a limitation that costs you influence, outcomes, and the recognition your insight genuinely deserves.",
    blindSpots:"Your passivity is not wisdom. It is avoidance dressed up as patience and thoughtfulness, and it is important that you recognise the difference. You tell yourself you are being measured, strategic, and considerate, but underneath those comfortable labels is a simple fear. Fear of conflict, of being wrong publicly, of asserting yourself in a way that might be challenged or rejected by others. This fear costs you deals, opportunities, and the influence your insight genuinely deserves. The label you put on your avoidance does not change its nature or reduce its cost.",
    howOthersSeeYou:"Insightful but passive, which creates a frustrating paradox for the people around you who recognise your value. People who know you well seek your counsel before big negotiations and value your perspective enormously. But at the table itself, you fade into the background when your voice is needed most. More assertive styles dominate the conversation while you sit with better ideas and deeper understanding, contributing only when directly asked and sometimes not even then. Your invisibility is not imposed on you by others. It is something you create through your own reluctance to step forward.",
    underPressure:"Under pressure, you disappear, not physically, but psychologically, becoming quieter, more analytical, and less engaged with the people in the room. You retreat into observation and analysis, contributing less and less as the stakes rise around you. At the exact moment when your insight is most valuable and most needed, you become least likely to share it. This pattern makes you unreliable in high pressure negotiations and limits the roles and opportunities available to you. Others learn that they cannot count on your voice when it matters most, which means they stop seeking it out.",
    watchOut:"Every other style at the table will outpace you if you do not find the courage to speak up and contribute actively. Dominators will take control of the conversation, Integrators will lead the process toward solutions, Yielders will at least build rapport and try to influence the emotional tone of the room, and you will sit there with the best understanding in the room watching suboptimal outcomes unfold because you chose silence over discomfort. Your silence is not neutrality. It is a choice, and it has real consequences for everyone at the table, including yourself. The cost of not speaking is always higher than you estimate in the moment.",
    growthEdge:"You have more power and more influence than you think, and the gap between your potential and your performance is entirely within your control to close. Make one firm statement or request in every negotiation, just one, not a question and not a suggestion, but a clear assertion of what you believe or what you need. Then build the muscle from there over time. Your insight combined with even moderate assertiveness would make you one of the most effective negotiators in any room you enter. The only thing standing between you and that reality is your willingness to speak up, tolerate the discomfort that follows, and discover that the consequences you fear almost never actually materialise.",
    growthSteps:[
      'Make one firm statement in your next negotiation that is not a question or a suggestion',
      'Prepare your talking points in advance including the exact moment you will share them',
      'Build the muscle by adding one more assertion each negotiation until it feels natural',
    ],
  },
};

const shadowLevels = [
  {title:'Clean Conscience',sub:'No shadow detected',color:'green',
    msg:"You answered every shadow question with transparency and integrity. What people see is what they get when they deal with you, and that is a genuine competitive advantage. This builds the kind of deep, structural trust that compounds over time into better deals, stronger relationships, and a reputation that opens doors before you even walk through them. In a world full of people playing games, your straightforwardness is rarer and more valuable than you might think. Protect this quality fiercely, because once lost it is almost impossible to rebuild."},
  {title:'Faint Shadow',sub:'A slight tendency detected',color:'green',
    msg:"You are overwhelmingly honest and transparent, but there was one moment where a less straightforward option appealed to you. This is entirely human and does not indicate a problem. Almost everyone has situations where the expedient path tempts them briefly. What matters is the overall pattern, and yours is strongly weighted toward integrity. Stay aware of the moments where convenience whispers louder than principle, because those are the moments that define your long term reputation."},
  {title:'Minor Shadow',sub:'Occasional strategic flexibility with the truth',color:'yellow',
    msg:"You are mostly authentic, but there are moments where you bend the truth, withhold key information, or mask your real agenda when it feels convenient. It is subtle enough that most people will not notice the pattern yet, but you should be aware that these tendencies grow stronger under pressure. What starts as a small omission or a harmless misdirection becomes a pattern of strategic dishonesty over time, and patterns eventually become visible to those who pay attention. The most dangerous negotiation habit is the one you do not acknowledge, because you cannot manage what you refuse to see."},
  {title:'Emerging Pattern',sub:'A noticeable tendency toward strategic deception',color:'yellow',
    msg:"A clear pattern is forming. You are comfortable using information selectively, presenting a carefully managed version of yourself, and allowing others to operate on assumptions you know to be incomplete or incorrect. You probably tell yourself this is just smart negotiation, and in isolated instances you might be right. But three out of five shadow indicators suggest this is becoming a default approach rather than a situational choice. The gap between who you present yourself as and how you actually operate is wide enough that perceptive counterparts will eventually notice it. The question is whether you close that gap voluntarily or wait until someone closes it for you."},
  {title:'The Mask Slips',sub:'A significant pattern of manipulation',color:'amber',
    msg:"You have a well developed pattern of presenting one face while operating with a different agenda underneath. You are likely winning short term gains through this approach, whether by hiding information, feigning warmth to extract concessions, or engineering situations where others act on incomplete or misleading information you have provided. But people talk, and reputations travel faster than you do in every industry. Four out of five shadow indicators is not a grey area. It is a clear pattern that experienced negotiators will recognise, discuss with colleagues, and prepare defences against. The trust you burn today is leverage you will never have access to tomorrow."},
  {title:'The Shadow Deepens',sub:'Strong manipulative tendencies detected',color:'red',
    msg:"Your results reveal a consistent and deliberate approach to manipulation. You exploit trust, fabricate or distort information, and manage perceptions in ways designed to extract advantage while maintaining a surface appearance of fairness. This is not occasional or situational. Five of five shadow indicators pointed to the same pattern. Here is the difficult truth. Experienced negotiators have met people who operate this way before, and they have defences ready. They share less, verify more, and build protective clauses into agreements specifically because of people who negotiate the way you do. Your approach has an expiry date, and it is closer than you think."},
  {title:'Dark Pattern',sub:'Deeply ingrained deceptive approach',color:'red',
    msg:"Six out of seven shadow questions revealed a pattern of deliberate deception, trust exploitation, and strategic manipulation. You consistently choose the path that extracts maximum advantage while maintaining plausible deniability, and you have likely been doing it long enough that it feels normal to you. It is not normal. It is a deeply ingrained habit that is costing you far more than you realise in lost trust, damaged relationships, and opportunities that never materialise because people warn each other about working with you. Senior negotiators, experienced buyers, and anyone who has been burned before will see through this approach faster than you expect. The question is not whether this catches up with you. It already has, in ways you probably cannot see from your current vantage point."},
  {title:'Deep Shadow',sub:'Pervasive manipulation across every indicator',color:'red',
    msg:"Every single shadow question pointed to the same pattern. You consistently choose manipulation over authenticity, deception over honesty, and the appearance of fairness over the reality of it. You see other people's openness as opportunity, their trust as a tool, and their goodwill as something to be harvested rather than reciprocated. This is not strategy. It is a fundamental orientation toward other people that will eventually isolate you professionally and personally. Every relationship you build on this foundation has a fracture line running through it, and the pressure required to break it gets smaller over time. Rebuilding trust is ten times harder than building it, so start now before the cost becomes unrecoverable. The skills behind this pattern are real and could be redirected toward building genuine influence that would make you formidable for all the right reasons."},
];

const styleLevels = {
  dominator: {
    negligible: "Competitive tactics are almost entirely absent from your negotiation approach. You rarely push back on terms, seldom anchor aggressively, and are unlikely to escalate when challenged at the table. You prefer other methods to reach agreement. This is not necessarily a weakness, but it means you may struggle to claim value when the deal genuinely requires assertiveness. If the other side pushes hard, you are unlikely to match their energy, which can result in leaving significant value on the table without realising it.",
    low: "You have some competitive instincts but they sit deep beneath the surface and rarely drive your behaviour at the table. You can push back when absolutely necessary, but it takes a lot of provocation to get there. Most counterparts will never see this side of you because your other negotiation tendencies activate long before your competitive instinct does. In deals where the other side is assertive, you may find yourself gradually giving ground rather than holding your position, not because you agree with their terms but because matching their energy feels unnatural to you.",
    moderate: "You have real competitive ability but it is not your default at the negotiation table. In most deals you defer to other approaches first. However, when pushed or when the stakes rise, your Dominator traits emerge and you become noticeably more assertive about terms. You are capable of pushing hard for what you want but it is not where you naturally start. This means you sometimes miss early opportunities to anchor strongly or set ambitious terms because your competitive instinct activates later in the conversation rather than from the opening position.",
    high: "Competition is a strong and visible part of how you negotiate. You push for favourable terms naturally, hold your ground under pressure, and are comfortable making the first offer and setting ambitious anchors. Counterparts notice your assertiveness early and adjust their approach accordingly. You rarely leave value on the table through timidity. The risk at this level is that your competitive energy can overshadow your other negotiation skills, making it harder to build the trust and rapport that complex deals often require.",
    dominant: "Competition is your natural state at the negotiation table. You instinctively push for the best possible terms, set the pace of every conversation, and resist concessions with genuine intensity. Every other style in your profile is filtered through this competitive lens first. This is not a mode you switch into when needed. It is where you live. Your counterparts know within minutes that they are dealing with someone who will fight for every point. This delivers strong short term results consistently, but it can make counterparts reluctant to negotiate with you again or share information openly.",
  },
  integrator: {
    negligible: "Collaborative and creative deal making is largely absent from your negotiation approach. You rarely explore underlying interests, seldom propose package deals or creative trades, and are unlikely to spend time understanding what the other side truly needs beyond their stated position. You tend to treat negotiations as straightforward exchanges of offers and counteroffers rather than opportunities to create additional value. This means you may consistently reach agreements that are acceptable but miss significantly better outcomes that a more exploratory conversation could have produced.",
    low: "You have some collaborative instincts but they rarely shape your behaviour at the table. You can engage in creative problem solving when someone else initiates it, but you are unlikely to lead the conversation in that direction yourself. When a deal stalls, your first instinct is not to explore new structures or look for creative trades. It is to push harder on existing terms or concede to move things forward. This means the value creating potential of most negotiations goes largely untapped when you are at the table.",
    moderate: "You have genuine collaborative ability that shows up in certain negotiation contexts but is not your consistent default. When you are comfortable with the counterpart or when the deal structure naturally invites creativity, you explore interests, propose trades, and look for mutual gains effectively. But under pressure or with difficult counterparts, this instinct retreats and your other styles take over. The collaborative deals you do build tend to be good ones. The question is whether you access this skill consistently enough to benefit from it across all your negotiations.",
    high: "Collaboration and creative deal making are a strong and visible part of how you negotiate. You naturally ask about underlying interests, propose package structures, and look for ways to expand the value available to both sides before dividing it. Counterparts find you genuinely engaged with their needs, not just your own, and this earns you access to information that more competitive negotiators never see. You consistently find deals that others miss because you are willing to invest time in understanding the full picture.",
    dominant: "Creative collaboration defines how you negotiate. Your first instinct in any deal is to understand interests, explore options, and build structures that create value for both sides. You see negotiations as joint problem solving exercises rather than competitive encounters, and this shapes every conversation you have at the table. Counterparts trust your process and share information willingly because they believe you genuinely care about their outcome. The risk is that your collaborative orientation can blind you to counterparts who are exploiting your openness.",
  },
  yielder: {
    negligible: "Relationship preservation and accommodation play almost no role in how you negotiate. You do not soften your position to maintain rapport, rarely worry about how the other side feels about the terms, and are unlikely to make concessions simply to keep the atmosphere positive. You focus on the deal substance rather than the interpersonal dynamic. This gives you clarity and efficiency at the table, but it also means you may miss opportunities that come from building genuine warmth with counterparts.",
    low: "You have some awareness of relationship dynamics at the table but they rarely drive your negotiation decisions. You can be warm and accommodating when it costs you nothing, but when the deal terms are at stake you prioritise substance over rapport without much hesitation. Counterparts may find you professional but not particularly warm, and they are unlikely to feel a strong personal connection with you after the negotiation.",
    moderate: "Relationships and harmony play a real but secondary role in your negotiations. You care about how counterparts feel and you make genuine efforts to keep interactions positive, but you can set those concerns aside when the deal requires it. You make some concessions to maintain rapport that a harder negotiator would not, but you do not give away the deal to keep everyone happy. This balance serves you well in most contexts.",
    high: "Maintaining positive relationships is a strong and visible priority in how you negotiate. You invest real energy in rapport, monitor how counterparts feel throughout the process, and adjust your approach to keep interactions comfortable and constructive. You are willing to sacrifice some deal value to preserve a relationship you believe matters long term. Counterparts genuinely enjoy negotiating with you, which gives you repeat business and referrals that harder negotiators never see.",
    dominant: "Relationships are the lens through which you see every negotiation. Your first priority is always the interpersonal dynamic, and deal terms come second. You will accept significantly worse terms rather than create tension with a counterpart you value, and you will make concessions that serve no strategic purpose simply to make the other side feel good about the process. Learning to separate your genuine care for people from your negotiation decisions is essential.",
  },
  calculator: {
    negligible: "Analysis and preparation play almost no role in your negotiation approach. You rarely research the other side, seldom build detailed positions in advance, and are unlikely to reference data or benchmarks during the conversation. You rely on instinct, relationships, or sheer force of will rather than evidence. This means you may miss important details in deal terms and agree to terms you would have rejected if you had done the homework.",
    low: "You do some preparation before negotiations but it is not thorough or consistent. You may review the key numbers and have a general sense of what you want, but you are unlikely to have modelled alternatives, researched the other side's constraints, or built a detailed walk away position. At the table, you rely on your other skills more than evidence.",
    moderate: "Analysis and preparation are a real part of your negotiation approach but not your defining feature. You do your homework on important deals, reference data when it supports your position, and can hold your own in detailed discussions about terms and benchmarks. However, you are equally comfortable making decisions based on instinct or relationship dynamics when the data is inconclusive.",
    high: "Thorough preparation and analytical rigour are a strong and visible part of how you negotiate. You arrive at the table with detailed research, clear benchmarks, modelled alternatives, and a precise understanding of your walk away position. Counterparts notice your preparation quickly and it earns you credibility and respect. You catch errors others miss and make decisions grounded in reality rather than hope.",
    dominant: "Analysis defines how you negotiate. You do not sit down at the table without thorough research, detailed models, and a comprehensive understanding of every variable at play. You trust evidence over intuition, process over personality, and logic over emotion in every deal. Your preparation gives you a genuine and consistent edge because you see things others miss.",
  },
};

const getStyleLevel = (score) => {
  if (score <= 1) return 'negligible';
  if (score <= 3) return 'low';
  if (score <= 6) return 'moderate';
  if (score <= 9) return 'high';
  return 'dominant';
};

const levelLabels = {
  negligible: { text: 'Negligible', color: '#94A3B8', bg: 'bg-gray-100', barWidth: '6%' },
  low: { text: 'Low', color: '#64748B', bg: 'bg-gray-200', barWidth: '25%' },
  moderate: { text: 'Moderate', color: '#3B82F6', bg: 'bg-blue-100', barWidth: '50%' },
  high: { text: 'High', color: '#F59E0B', bg: 'bg-amber-100', barWidth: '75%' },
  dominant: { text: 'Dominant', color: '#EF4444', bg: 'bg-red-100', barWidth: '100%' },
};

const styleMeta = {
  dominator:{label:'Dominator',color:'#DC2626',tw:'text-white',bg:'bg-red-600',light:'bg-red-600',border:'border-red-600',barColor:'#DC2626',
    brief:'The competitive negotiator who leads with assertiveness, sets ambitious terms, and pushes to win.'},
  integrator:{label:'Integrator',color:'#9333EA',tw:'text-white',bg:'bg-purple-600',light:'bg-purple-600',border:'border-purple-600',barColor:'#9333EA',
    brief:'The collaborative negotiator who explores interests, builds creative deals, and seeks value for both sides.'},
  yielder:{label:'Yielder',color:'#16A34A',tw:'text-white',bg:'bg-green-600',light:'bg-green-600',border:'border-green-600',barColor:'#16A34A',
    brief:'The relationship focused negotiator who prioritises harmony, builds trust, and maintains rapport at the table.'},
  calculator:{label:'Calculator',color:'#2563EB',tw:'text-white',bg:'bg-blue-600',light:'bg-blue-600',border:'border-blue-600',barColor:'#2563EB',
    brief:'The analytical negotiator who leads with data, preparation, and evidence based decision making.'},
};

const spottingGuide = {
  dominator: {
    pace:'Fast',
    tone:'Direct and assertive',
    focus:'Winning the outcome',
    phrases:['"Bottom line"','"I need"','"Let\'s close this"'],
    behaviours:['Anchors high','Pushes deadlines','Interrupts often']
  },
  integrator: {
    pace:'Moderate',
    tone:'Curious and collaborative',
    focus:'Creative solutions',
    phrases:['"What if we"','"Both sides"','"How about"'],
    behaviours:['Asks about interests','Proposes packages','Seeks common ground']
  },
  yielder: {
    pace:'Slow',
    tone:'Gentle and agreeable',
    focus:'Harmony and rapport',
    phrases:['"That\'s fair"','"Whatever works"','"I understand"'],
    behaviours:['Concedes early','Avoids silence','Apologises often']
  },
  calculator: {
    pace:'Deliberate',
    tone:'Measured and precise',
    focus:'Evidence and analysis',
    phrases:['"The data shows"','"I need to check"','"Based on this"'],
    behaviours:['Requests time','Cites benchmarks','Brings documents']
  }
};

const matchupAdvice = {
  'dominator-integrator': {
    tells:[
      'They ask lots of questions before committing to anything',
      'They propose packages and creative trades rather than single item demands',
      'They use phrases like "what if" and "how about" frequently',
      'They focus on underlying interests rather than stated positions'
    ],
    advantages:[
      'Your decisiveness cuts through their lengthy exploration process',
      'You set the pace and they often follow your energy',
      'You are not afraid to push when the moment calls for it'
    ],
    risks:[
      'Dismissing their ideas too quickly and missing options that could benefit you',
      'Appearing unreasonable by refusing to explore alternatives',
      'Mistaking their patience for weakness when it is actually strategy'
    ],
    playbook:[
      'Let them explore options but set a clear deadline for decisions',
      'Treat their questions as information gathering, not stalling',
      'Propose your own packages rather than just rejecting theirs'
    ],
    fakeWarning:'If their proposals consistently favour them despite collaborative language, they are using integration as a cover for extraction. Watch whether the "creative solutions" actually improve your position or just repackage their demands in friendlier terms.'
  },
  'dominator-yielder': {
    tells:[
      'They speak softly and concede points early in the conversation',
      'They check whether you are comfortable and whether things feel fair',
      'They avoid silence and fill gaps with concessions or apologies',
      'They use phrases like "that\'s fair" and "whatever works for you"'
    ],
    advantages:[
      'You naturally control the pace and direction of the conversation',
      'They share information freely because they want to build trust',
      'You can anchor strongly and they are unlikely to push back hard'
    ],
    risks:[
      'Pushing so hard they agree to things they cannot actually deliver',
      'Destroying a relationship you might need for future deals',
      'Gaining a reputation as someone who exploits weaker counterparts'
    ],
    playbook:[
      'Moderate your intensity because they will likely give you what you want anyway',
      'Ask what they need rather than simply taking everything on the table',
      'A slightly softer approach here gets you a more durable and implementable deal'
    ],
    fakeWarning:'If a yielder suddenly stands firm on one specific point after giving everything else away, that point may be the real prize they were protecting all along. Their earlier concessions may have been strategic, designed to make you feel satisfied before the real negotiation begins.'
  },
  'dominator-calculator': {
    tells:[
      'They speak at a deliberate pace and pause before responding',
      'They reference data, benchmarks, and precedents frequently',
      'They request adjournments or additional time to review',
      'They bring documents and show minimal emotional expression'
    ],
    advantages:[
      'Your speed pressures their process and forces faster decisions',
      'Your confidence can shift momentum when their analysis is inconclusive',
      'You make decisions faster which lets you control the agenda'
    ],
    risks:[
      'Making claims they will fact check and disprove, damaging your credibility',
      'Losing respect if your numbers are wrong or your preparation is thin',
      'Underestimating how thoroughly they have prepared for this conversation'
    ],
    playbook:[
      'Bring solid data because they will challenge every claim that lacks evidence',
      'Respect their need for time or they will dig in harder out of principle',
      'Use their own analysis to support your position whenever possible'
    ],
    fakeWarning:'If they present data that perfectly supports only their position without acknowledging any counterpoints, their objectivity is a performance. Genuine analysis includes uncertainty. If theirs has none, it has been curated to persuade, not to inform.'
  },
  'integrator-dominator': {
    tells:[
      'They speak fast, interrupt, and push for quick decisions',
      'They use direct language like "I need" and "bottom line"',
      'They anchor high and make the first offer confidently',
      'They maintain strong eye contact and take up physical space'
    ],
    advantages:[
      'You see creative options they miss because they are focused only on winning',
      'You read the room better and notice dynamics they overlook entirely',
      'You can reframe their demands into opportunities that serve both sides'
    ],
    risks:[
      'Getting bulldozed while you are still exploring options',
      'Overthinking while they act decisively and claim the value on the table',
      'Confusing their confidence with being right about the substance'
    ],
    playbook:[
      'Acknowledge their position first before redirecting the conversation',
      'Ask "why" to slow their pace and uncover the interests behind their demands',
      'Propose packages that give them a visible win while protecting your priorities'
    ],
    fakeWarning:'If they suddenly turn warm and collaborative after being aggressive, check whether their proposals match their new tone. Real collaboration shows up in the deal structure, not just the conversation. Words are cheap. Numbers are honest.'
  },
  'integrator-yielder': {
    tells:[
      'They agree quickly and avoid pushing back on your proposals',
      'They use gentle language and check frequently if you are happy',
      'They concede early and often to maintain a positive atmosphere',
      'They apologise unnecessarily and fill silences with soft concessions'
    ],
    advantages:[
      'They trust you naturally and share information willingly',
      'They genuinely want to find common ground which aligns with your approach',
      'Your creative proposals will be received openly and without resistance'
    ],
    risks:[
      'Reaching agreement too easily without testing whether the deal is truly optimal',
      'Letting their accommodation mask unexplored value that both sides could share',
      'Mistaking their quick agreement for genuine satisfaction with the terms'
    ],
    playbook:[
      'Gently push past their first offer because they almost always have more room',
      'Ask what would make this deal great for them, not just acceptable',
      'Protect them from over conceding because it will damage the deal long term'
    ],
    fakeWarning:'If their constant agreeableness comes with subtle guilt trips or obligations attached, they may be using warmth as a tool to create debt you will be expected to repay. Genuine yielders give freely. Strategic ones keep a ledger.'
  },
  'integrator-calculator': {
    tells:[
      'They respond slowly and ask for time to review your proposals',
      'They reference benchmarks, market rates, and historical data',
      'They show minimal emotional reaction to your ideas',
      'They arrive with documents and want to work through details methodically'
    ],
    advantages:[
      'Your creativity complements their analytical rigour beautifully',
      'You bring warmth and rapport that they lack naturally',
      'You can build trust while they are still processing the numbers'
    ],
    risks:[
      'Frustrating them by moving too fast or proposing ideas without supporting evidence',
      'Dismissing their need for data as excessive caution or stalling',
      'Proposing solutions before they are ready to evaluate them properly'
    ],
    playbook:[
      'Lead with data before leading with ideas to earn their respect first',
      'Give them time to process your proposals rather than expecting immediate responses',
      'Frame creative options with supporting evidence and they will become your strongest allies'
    ],
    fakeWarning:'If they use complexity and data volume to overwhelm rather than clarify, they may be using analysis as a weapon to confuse you into accepting unfavourable terms. Genuine analysis simplifies decisions. Strategic analysis complicates them.'
  },
  'yielder-dominator': {
    tells:[
      'They speak fast, interrupt, and push the conversation toward quick decisions',
      'They use direct and sometimes blunt language without softening it',
      'They anchor high and make demands early and confidently',
      'They maintain intense eye contact and dominate the physical space'
    ],
    advantages:[
      'Your warmth disarms them and they may reveal more than they intended',
      'They underestimate you which means they prepare less carefully for you',
      'You read their emotional state better than they read yours'
    ],
    risks:[
      'Folding under pressure and agreeing to terms you will regret later',
      'Mistaking their confidence for authority they do not actually have',
      'Allowing their pace to override your own needs and boundaries'
    ],
    playbook:[
      'Write your non negotiables down before you walk in and do not move from them',
      'Let them push first, then respond with a calm but firm position',
      'Remember that their aggression is a style, not reality. The deal is rarely as urgent as they claim'
    ],
    fakeWarning:'If a dominator suddenly becomes your friend, complimenting you and asking personal questions, they are likely softening you up before a significant demand. When the shift comes, return to your written boundaries and do not let flattery move your position.'
  },
  'yielder-integrator': {
    tells:[
      'They ask lots of questions and explore multiple angles before committing',
      'They propose creative solutions and use "we" language naturally',
      'They focus on interests and underlying needs rather than stated positions',
      'They maintain a moderate pace and seem genuinely curious about your perspective'
    ],
    advantages:[
      'You build trust faster than they expect which opens doors to real information',
      'They genuinely value the relationship and will invest in maintaining it',
      'They will share information willingly because they believe in open exchange'
    ],
    risks:[
      'Over trusting their collaborative approach without verifying the deal serves your interests',
      'Agreeing to complex structures you do not fully understand',
      'Deferring to their expertise when you should be advocating for yourself'
    ],
    playbook:[
      'Ask them to explain every element of complex proposals until you fully understand',
      'Check whether their creative solutions actually give you what you need, not just what sounds fair',
      'Your warmth plus their strategy can produce excellent outcomes so stay engaged rather than deferring'
    ],
    fakeWarning:'If they consistently propose "win win" solutions where the wins are unequal and always in their favour, their collaboration is a negotiation technique, not a genuine value. Trust the numbers in the deal, not the language around it.'
  },
  'yielder-calculator': {
    tells:[
      'They respond at a deliberate pace and think carefully before speaking',
      'They reference data and ask detailed questions about specifics',
      'They show little emotional engagement and stay neutral throughout',
      'They request time to review and bring prepared documents to every meeting'
    ],
    advantages:[
      'Your warmth helps them relax and they open up more than usual',
      'You notice emotional cues they miss in themselves and others',
      'You create a comfortable atmosphere that draws out better information'
    ],
    risks:[
      'Feeling dismissed by their lack of emotional engagement with you',
      'Interpreting their analytical detachment as disinterest or disrespect',
      'Conceding on points where your own data would support a stronger position'
    ],
    playbook:[
      'Prepare your own data because they take you more seriously when you speak their language',
      'Do not fill silences with concessions. Their pauses are for processing, not pressuring you',
      'Ask them to walk you through their analysis so you can spot where to negotiate'
    ],
    fakeWarning:'If they present mountains of data that always supports their position, ask to see the assumptions behind the numbers. Selective data is a powerful form of manipulation disguised as objectivity. Fair analysis includes data that challenges their own position too.'
  },
  'calculator-dominator': {
    tells:[
      'They speak fast and push for quick decisions before you feel ready',
      'They use direct language, make demands, and apply time pressure',
      'They anchor high and lead with confident assertions about what they need',
      'They show impatience with detailed analysis and want to move forward immediately'
    ],
    advantages:[
      'Your preparation exposes gaps in their arguments they cannot bluff through',
      'Your data gives you solid ground to stand on when they apply pressure',
      'Your patience outlasts their intensity if you hold your nerve'
    ],
    risks:[
      'Retreating into analysis when you need to engage and respond directly',
      'Being too slow to respond to their quick tactical moves',
      'Winning the logical argument but losing the room emotionally'
    ],
    playbook:[
      'Present your strongest data point early to establish credibility and earn their respect',
      'Match their decisiveness with confident positions backed by solid evidence',
      'When they apply pressure, respond with facts not emotions. They respect substance over style'
    ],
    fakeWarning:'If their aggression spikes after you present strong evidence, they are likely bluffing because your data has weakened their position. Hold firm and let the silence work. A dominator who gets louder is usually a dominator who is losing ground.'
  },
  'calculator-integrator': {
    tells:[
      'They ask about your interests and underlying needs, not just your stated position',
      'They propose creative packages and multi item trades',
      'They use collaborative language and invite joint problem solving',
      'They maintain a moderate pace and seem genuinely curious about your perspective'
    ],
    advantages:[
      'Your analysis grounds their creativity in reality and prevents wishful thinking',
      'You spot risks in their proposals that others miss entirely',
      'Your rigour ensures any agreement is implementable and durable over time'
    ],
    risks:[
      'Dismissing creative options because they lack immediate data support',
      'Being too rigid about process while they build relationships around you',
      'Appearing cold and disengaged while they connect with everyone else in the room'
    ],
    playbook:[
      'Evaluate their proposals on merit rather than dismissing what you cannot immediately quantify',
      'Combine your analytical strength with their creative ideas for solutions that are both innovative and robust',
      'Let them handle the relationship building while you ensure the deal structure is sound'
    ],
    fakeWarning:'If they frame every concession they want as a "creative solution" or "mutual benefit," run the numbers independently. Genuine integration improves both positions. Strategic integration improves only theirs while sounding like it helps everyone.'
  },
  'calculator-yielder': {
    tells:[
      'They speak softly and agree quickly without challenging your points',
      'They defer to your expertise and rarely question your data or conclusions',
      'They avoid confrontation and fill silences with soft concessions',
      'They apologise unnecessarily and check frequently whether things feel fair'
    ],
    advantages:[
      'They respect your preparation and take your analysis seriously from the start',
      'They share information freely because they want to build a trusting relationship',
      'They will defer to well presented evidence based arguments without resistance'
    ],
    risks:[
      'Steamrolling them with data and getting a deal they cannot actually implement',
      'Mistaking their agreement for genuine understanding and commitment to the terms',
      'Overlooking their concerns because they are too polite to raise them directly'
    ],
    playbook:[
      'Slow your analysis down and check they understand and genuinely agree, not just comply',
      'Ask what concerns they have rather than waiting for objections they will never volunteer',
      'A deal they fully understand and support will be implemented far better than one they simply accepted'
    ],
    fakeWarning:'If they agree with everything but the deal stalls during implementation, they may have agreed to end the discomfort of negotiating rather than because they genuinely supported the terms. Silence from a yielder does not mean satisfaction. It often means suppression.'
  }
};

function calcResults(answers){
  const sc={dominator:0,integrator:0,yielder:0,calculator:0};
  let sh=0;
  answers.forEach((oi,qi)=>{
    const q=questions[qi];
    if(q.type==='style') sc[q.options[oi].map]++;
    else if(q.options[oi].shadow) sh++;
  });
  const sorted=Object.entries(sc).sort((a,b)=>b[1]-a[1]);
  const topScore=sorted[0][1];
  const tied=sorted.filter(([_,v])=>v===topScore).map(([k])=>k);
  if(tied.length>1) return {scores:sc,shadow:sh,tied};
  const p=sorted[0][0], s=sorted[1][0];
  return {scores:sc,shadow:sh,primary:p,secondary:s,archetype:archetypes[p+'-'+s]};
}

function genSVGPetal(sc) {
  const cx = 200, cy = 200, maxR = 120, ringOuterR = 158, ringInnerR = 134, total = 16;
  const styles = [
    {key:'dominator', label:'DOMINATOR', color:'#DC2626', light:'#FEE2E2', angle:-Math.PI/2},
    {key:'integrator', label:'INTEGRATOR', color:'#9333EA', light:'#F3E8FF', angle:0},
    {key:'yielder', label:'YIELDER', color:'#16A34A', light:'#DCFCE7', angle:Math.PI/2},
    {key:'calculator', label:'CALCULATOR', color:'#2563EB', light:'#DBEAFE', angle:Math.PI},
  ];

  const arcPath = (sa, ea, iR, oR) => {
    const sx = cx + oR * Math.cos(sa), sy = cy + oR * Math.sin(sa);
    const ex = cx + oR * Math.cos(ea), ey = cy + oR * Math.sin(ea);
    const ix = cx + iR * Math.cos(ea), iy = cy + iR * Math.sin(ea);
    const jx = cx + iR * Math.cos(sa), jy = cy + iR * Math.sin(sa);
    return `M${sx},${sy} A${oR},${oR} 0 0 1 ${ex},${ey} L${ix},${iy} A${iR},${iR} 0 0 0 ${jx},${jy}Z`;
  };
  const wedgeD = (sa, ea, r) => {
    const sx = cx + r * Math.cos(sa), sy = cy + r * Math.sin(sa);
    const ex = cx + r * Math.cos(ea), ey = cy + r * Math.sin(ea);
    return `M${cx},${cy} L${sx},${sy} A${r},${r} 0 0 1 ${ex},${ey}Z`;
  };

  let defs = '';
  styles.forEach(s => {
    const r = (ringOuterR + ringInnerR) / 2, spread = Math.PI / 4.5;
    if (s.angle === Math.PI) {
      const sa = s.angle + spread, ea = s.angle - spread;
      defs += `<path id="tp-${s.key}" d="M${cx + r * Math.cos(sa)},${cy + r * Math.sin(sa)} A${r},${r} 0 0 0 ${cx + r * Math.cos(ea)},${cy + r * Math.sin(ea)}"/>`;
    } else {
      const sa = s.angle - spread, ea = s.angle + spread;
      defs += `<path id="tp-${s.key}" d="M${cx + r * Math.cos(sa)},${cy + r * Math.sin(sa)} A${r},${r} 0 0 1 ${cx + r * Math.cos(ea)},${cy + r * Math.sin(ea)}"/>`;
    }
  });

  const bg = `<circle cx="${cx}" cy="${cy}" r="${ringOuterR}" fill="#FAFAFA"/>`;

  const bgWedges = styles.map(s =>
    `<path d="${wedgeD(s.angle - Math.PI/4, s.angle + Math.PI/4, ringInnerR)}" fill="${s.light}" opacity="0.35"/>`
  ).join('');

  const guides = [0.25, 0.5, 0.75, 1].map(f =>
    `<circle cx="${cx}" cy="${cy}" r="${maxR * f}" fill="none" stroke="#E2E8F0" stroke-width="0.75"/>`
  ).join('');

  const scoreWedges = styles.map(s => {
    const r = Math.max((sc[s.key] / total) * maxR, 8);
    return `<path d="${wedgeD(s.angle - Math.PI/4, s.angle + Math.PI/4, r)}" fill="${s.color}" opacity="1"/>`;
  }).join('');

  const dividers = styles.map(s => {
    const a = s.angle - Math.PI / 4;
    return `<line x1="${cx}" y1="${cy}" x2="${cx + ringInnerR * Math.cos(a)}" y2="${cy + ringInnerR * Math.sin(a)}" stroke="white" stroke-width="3" opacity="0.9"/>`;
  }).join('');

  const center = `<circle cx="${cx}" cy="${cy}" r="7" fill="white" stroke="#E5E7EB" stroke-width="1.5"/><circle cx="${cx}" cy="${cy}" r="3" fill="#94A3B8" opacity="0.4"/>`;

  const rings = styles.map(s =>
    `<path d="${arcPath(s.angle - Math.PI/4 + 0.02, s.angle + Math.PI/4 - 0.02, ringInnerR, ringOuterR)}" fill="${s.color}" opacity="1" stroke="white" stroke-width="2"/>`
  ).join('');

  const labels = styles.map(s =>
    `<text><textPath href="#tp-${s.key}" startOffset="50%" text-anchor="middle" fill="white" font-size="11" font-weight="bold" font-family="system-ui,sans-serif" letter-spacing="2">${s.label}</textPath></text>`
  ).join('');

  const scores = styles.map(s => {
    const lR = ringOuterR + 18;
    const pct = Math.round((sc[s.key] / total) * 100);
    return `<text x="${cx + lR * Math.cos(s.angle)}" y="${cy + lR * Math.sin(s.angle) + 4}" text-anchor="middle" fill="${s.color}" font-size="12" font-weight="bold" font-family="system-ui,sans-serif">${pct}%</text>`;
  }).join('');

  return `<svg viewBox="0 0 400 400" width="400" height="400" xmlns="http://www.w3.org/2000/svg"><defs>${defs}</defs>${bg}${bgWedges}${guides}${scoreWedges}${dividers}${center}${rings}${labels}${scores}</svg>`;
}

const sanitize = (str) => str.replace(/[<>&"']/g, c => ({
  '<':'&lt;', '>':'&gt;', '&':'&amp;', '"':'&quot;', "'":'&#39;'
}[c]));
function genHTML(r, name) {
  const a = r.archetype;
  const sc = r.scores;
  const sl = shadowLevels[r.shadow];
  const p = r.primary;
  const s = r.secondary;
  const total = 16;
  const pct = (k) => Math.round((sc[k] / total) * 100);
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const assessmentId = `NP-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 99999)).padStart(5, '0')}`;
  const displayName = name ? sanitize(name) : 'Assessment Participant';

  const getIntensity = (style) => {
    const val = pct(style);
    if (val >= 35) return 'High';
    if (val >= 20) return 'Moderate';
    return 'Low';
  };

  // Sort styles by score descending
  const sortedStyles = ['dominator', 'integrator', 'yielder', 'calculator'].sort((a, b) => sc[b] - sc[a]);

  const scoreRowsHTML = sortedStyles.map((style, i) => `
    <div class="score-row">
      <div class="score-label">${styleMeta[style].label}</div>
      <div class="score-bar-bg"><div class="score-bar-fill" style="width:${pct(style)}%;opacity:${i === 0 ? '1' : i === 1 ? '0.7' : '0.4'}"></div></div>
      <div><div class="score-percent">${pct(style)}%</div><div class="score-intensity">${getIntensity(style)}</div></div>
    </div>`).join('');

  // Shadow dots
  const shadowDotsHTML = [1, 2, 3, 4, 5].map(i =>
    `<div class="shadow-dot ${i <= r.shadow ? 'active' : ''}"></div>`
  ).join('');

  // Format text into paragraphs
  const fmtParas = (text) => {
    if (!text) return '';
    return text.split(/\n\n/).filter(p => p.trim()).map(p =>
      `<p class="body-text">${p.trim()}</p>`
    ).join('');
  };

  // Extract first sentence as lead quote
  const extractLead = (text) => {
    if (!text) return { lead: '', rest: '' };
    const dot = text.indexOf('.');
    if (dot === -1) return { lead: '', rest: text };
    return { lead: text.substring(0, dot + 1), rest: text.substring(dot + 1).trim() };
  };

  // Primary/secondary style formatting
  const primaryParts = extractLead(stylePrimary[p]);
  const secondaryParts = extractLead(styleSecondary[s]);

  // Strengths/weaknesses as bullet items
  const strengthItems = a.strengths.split('. ').filter(s => s.trim().length > 10).map(s =>
    `<div class="bullet-item"><span class="bullet-icon">✓</span><p>${s.trim().replace(/\.$/, '')}.</p></div>`
  ).join('');

  const weaknessItems = a.weaknesses.split('. ').filter(s => s.trim().length > 10).map(s =>
    `<div class="bullet-item"><span class="bullet-icon">✗</span><p>${s.trim().replace(/\.$/, '')}.</p></div>`
  ).join('');

  // Growth steps
  const growthStepsHTML = a.growthSteps.map((step, i) =>
    `<div class="growth-step"><div class="growth-num">${i + 1}</div><p>${step}</p></div>`
  ).join('');

// Style intensity section - split into two clean parts (2 cards each)
const styleOrder = ['dominator', 'integrator', 'yielder', 'calculator'];

const createIntensityCard = (style) => {
  const score = sc[style];
  const level = score <= 1 ? 'negligible' : score <= 3 ? 'low' : score <= 6 ? 'moderate' : score <= 9 ? 'high' : 'dominant';
  const levelText = { negligible: 'Negligible', low: 'Low', moderate: 'Moderate', high: 'High', dominant: 'Dominant' }[level];
  const pctVal = pct(style);
  const desc = styleLevels[style][level];
  return `<div class="intensity-card">
    <div class="intensity-header">
      <div class="intensity-name">${styleMeta[style].label}</div>
      <div class="intensity-meta"><span class="intensity-level">${levelText}</span><span class="intensity-pct">${pctVal}%</span></div>
    </div>
    <div class="bar-bg"><div class="bar-fill" style="width:${pctVal}%"></div></div>
    <p class="intensity-brief">${styleMeta[style].brief}</p>
    <p class="intensity-desc">${desc}</p>
  </div>`;
};

const intensityPart1 = styleOrder.slice(0, 2).map(createIntensityCard).join('');
const intensityPart2 = styleOrder.slice(2, 4).map(createIntensityCard).join('');

  // Spotting guide - NO COLOURS
  const spotStyles = ['dominator', 'integrator', 'yielder', 'calculator'];
  const spotGridHTML = spotStyles.map(style => {
    const sg = spottingGuide[style];
    const lab = styleMeta[style].label;
    return `<div class="spot-card">
      <div class="spot-title">${lab}</div>
      <table class="spot-table">
        <tr><td class="spot-key">Pace:</td><td>${sg.pace}</td></tr>
        <tr><td class="spot-key">Tone:</td><td>${sg.tone}</td></tr>
        <tr><td class="spot-key">Focus:</td><td>${sg.focus}</td></tr>
      </table>
      <p class="spot-subhead">You will hear:</p>
      <div class="spot-phrases">${sg.phrases.map(ph => `<span class="spot-phrase">"${ph}"</span>`).join('')}</div>
      <p class="spot-subhead">They will:</p>
      ${sg.behaviours.map(b => `<p class="spot-behaviour">• ${b}</p>`).join('')}
    </div>`;
  }).join('');

  // Matchup cards - NO COLOURS
  const opponents = spotStyles.filter(st => st !== p);
  const matchupCardsHTML = opponents.map(opp => {
    const key = p + '-' + opp;
    const m = matchupAdvice[key];
    const oppLab = styleMeta[opp].label;
    const myLab = styleMeta[p].label;
    return `<div class="matchup-card">
      <div class="matchup-header">
        <span class="matchup-you">You (${myLab})</span>
        <span class="matchup-vs">vs</span>
        <span class="matchup-them">${oppLab}</span>
      </div>
      <div class="matchup-body">
        <div class="matchup-section-label">Their Tells</div>
        ${m.tells.map(t => `<p class="matchup-item">• ${t}</p>`).join('')}
        <div class="matchup-columns">
          <div>
            <div class="matchup-section-label">Your Advantages</div>
            ${m.advantages.map(av => `<p class="matchup-item">✓ ${av}</p>`).join('')}
          </div>
          <div>
            <div class="matchup-section-label">Your Risks</div>
            ${m.risks.map(ri => `<p class="matchup-item">✗ ${ri}</p>`).join('')}
          </div>
        </div>
        <div class="matchup-section-label">Your Playbook</div>
        ${m.playbook.map((step, i) => `<p class="matchup-item"><strong>${i + 1}.</strong> ${step}</p>`).join('')}
        <div class="matchup-warning">
          <div class="matchup-warning-label">⚠ Watch For The Fake Style</div>
          <p>${m.fakeWarning}</p>
        </div>
      </div>
    </div>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Negotiation Profile Report – ${displayName}</title>
  <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
 <style>
  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --navy: #1B2A4A;
    --navy-light: #2C3E5A;
    --gold: #B8860B;
    --gold-light: #D4A843;
    --charcoal: #3A3A3A;
    --grey-light: #F7F8FA;
    --grey-mid: #E2E4E8;
    --grey-text: #6B7280;
    --white: #FFFFFF;
  }

  body {
    font-family: 'Inter', -apple-system, sans-serif;
    color: var(--charcoal);
    background: var(--white);
    font-size: 15px;
    line-height: 1.7;
    font-weight: 300;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .page {
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto;
    padding: 50px 60px 90px 60px;   /* top | right | bottom | left  ← more bottom space for footer */
    position: relative;
    page-break-after: always;
    page-break-inside: avoid;
  }

  .page:last-child { page-break-after: auto; }

  /* === COVER === */
  .cover {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: var(--navy);
    color: var(--white);
    padding: 70px 60px;
  }

  .cover-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .cover-label {
    font-size: 11px;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: var(--gold-light);
    font-weight: 500;
  }

  .cover-confidential {
    font-size: 10px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: rgba(255,255,255,0.4);
    border: 1px solid rgba(255,255,255,0.2);
    padding: 6px 14px;
  }

  .cover-middle {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .cover-middle h1 {
    font-family: 'Playfair Display', serif;
    font-size: 48px;
    font-weight: 700;
    margin-bottom: 16px;
    line-height: 1.15;
  }

  .cover-middle .subtitle {
    font-size: 17px;
    font-weight: 300;
    color: rgba(255,255,255,0.7);
    margin-bottom: 36px;
  }

  .cover-divider {
    width: 60px;
    height: 2px;
    background: var(--gold);
    margin-bottom: 36px;
  }

  .cover-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  .cover-detail-label {
    font-size: 10px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: rgba(255,255,255,0.4);
    margin-bottom: 4px;
  }

  .cover-detail-value {
    font-size: 15px;
    font-weight: 400;
  }

  .cover-bottom {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .cover-brand {
    font-size: 12px;
    letter-spacing: 1px;
    color: rgba(255,255,255,0.5);
  }

  /* === PAGE HEADER === */
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 36px;          /* breathing room under header */
    padding-bottom: 14px;
    border-bottom: 1px solid var(--grey-mid);
  }

  .page-header-brand,
  .page-header-section {
    font-size: 10px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--grey-text);
  }

  /* === PAGE FOOTER (improved) === */
  .page-footer {
    position: absolute;
    bottom: 28px;
    left: 60px;
    right: 60px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 12px;
    border-top: 1px solid var(--grey-mid);
    font-size: 10px;
    color: var(--grey-text);
  }

  /* === SECTION LABELS === */
  .section-number {
    font-size: 11px;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: var(--gold);
    font-weight: 600;
    margin-bottom: 8px;
  }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: 28px;
    font-weight: 700;
    color: var(--navy);
    margin-bottom: 8px;
  }

  .section-subtitle {
    font-size: 15px;
    color: var(--grey-text);
    font-weight: 300;
    margin-bottom: 28px;
  }

  /* === BADGES === */
  .archetype-badges {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
  }

  .archetype-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--grey-light);
    border: 1px solid var(--grey-mid);
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 500;
  }

  .archetype-badge .label {
    font-size: 10px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--grey-text);
  }

  .archetype-badge .value {
    color: var(--navy);
    font-weight: 600;
  }

  /* === SCORES === */
  .scores { margin-top: 32px; }

  .score-row {
    display: grid;
    grid-template-columns: 120px 1fr 70px;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }

  .score-label {
    font-size: 13px;
    font-weight: 500;
    color: var(--navy);
  }

  .score-bar-bg {
    height: 6px;
    background: var(--grey-light);
    border-radius: 3px;
    overflow: hidden;
  }

  .score-bar-fill {
    height: 100%;
    border-radius: 3px;
    background: var(--navy);
  }

  .score-percent {
    font-size: 13px;
    font-weight: 600;
    color: var(--navy);
    text-align: right;
  }

  .score-intensity {
    font-size: 10px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--grey-text);
    text-align: right;
  }

  /* === ARCHETYPE DESC === */
  .archetype-desc {
    margin-top: 32px;
    padding: 28px;
    background: var(--grey-light);
    border-left: 3px solid var(--gold);
  }

  .archetype-desc p {
    font-size: 14px;
    line-height: 1.75;
    color: var(--charcoal);
    margin-bottom: 12px;
  }

  .archetype-desc p:last-child { margin-bottom: 0; }

  /* === BODY TEXT === */
  .style-lead {
    font-size: 16px;
    font-weight: 400;
    font-style: italic;
    color: var(--navy);
    padding-left: 18px;
    border-left: 3px solid var(--gold);
    margin-bottom: 24px;
    line-height: 1.6;
  }

  .body-text {
    font-size: 14px;
    line-height: 1.8;
    color: var(--charcoal);
    margin-bottom: 16px;
  }

  .body-text:last-child { margin-bottom: 0; }

  /* === HIGHLIGHT BOX === */
  .highlight-box {
    padding: 24px 28px;
    background: var(--grey-light);
    border-left: 3px solid var(--gold);
    margin-bottom: 22px;
  }

  .highlight-box h3 {
    font-size: 14px;
    font-weight: 600;
    color: var(--navy);
    margin-bottom: 10px;
  }

  .highlight-box p {
    font-size: 13.5px;
    line-height: 1.75;
    color: var(--charcoal);
    margin-bottom: 10px;
  }

  .highlight-box p:last-child { margin-bottom: 0; }

  /* === BULLET ITEMS === */
  .bullet-item {
    display: flex;
    gap: 10px;
    margin-bottom: 10px;
    align-items: flex-start;
  }

  .bullet-icon {
    font-weight: 700;
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--navy);
  }

  .bullet-item p {
    font-size: 13px;
    line-height: 1.65;
    color: var(--charcoal);
  }

  /* === TWO COLUMN === */
  .two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 28px;
    margin-bottom: 24px;
  }

  .col-box {
    padding: 22px;
    background: var(--grey-light);
    border-left: 3px solid var(--navy);
  }

  .col-box h4 {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--navy);
  }

  /* === GROWTH STEPS === */
  .growth-step {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 14px;
  }

  .growth-num {
    min-width: 26px;
    height: 26px;
    background: var(--navy);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 12px;
    flex-shrink: 0;
  }

  .growth-step p {
    font-size: 13px;
    line-height: 1.65;
    color: var(--charcoal);
    padding-top: 3px;
  }

  /* === SHADOW === */
  .shadow-meter {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 28px;
    padding: 20px 28px;
    background: var(--navy);
    border-radius: 4px;
  }

  .shadow-meter-label {
    font-size: 12px;
    font-weight: 500;
    color: rgba(255,255,255,0.6);
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .shadow-dots {
    display: flex;
    gap: 8px;
  }

  .shadow-dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,0.3);
    background: transparent;
  }

  .shadow-dot.active {
    background: var(--gold);
    border-color: var(--gold);
  }

  .shadow-score-text {
    font-size: 14px;
    font-weight: 600;
    color: var(--gold-light);
  }

  /* === INTENSITY CARDS === */
  .intensity-card {
    border: 1px solid var(--grey-mid);
    padding: 18px 22px;
    margin-bottom: 14px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .intensity-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .intensity-name {
    font-weight: 600;
    font-size: 14px;
    color: var(--navy);
  }

  .intensity-meta {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .intensity-level {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    background: var(--grey-light);
    color: var(--grey-text);
  }

  .intensity-pct {
    font-size: 14px;
    font-weight: 700;
    color: var(--navy);
  }

  .bar-bg {
    height: 6px;
    background: var(--grey-light);
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 10px;
  }

  .bar-fill {
    height: 100%;
    border-radius: 3px;
    background: var(--navy);
  }

  .intensity-brief {
    font-size: 13px;
    font-weight: 600;
    color: var(--navy);
    margin-bottom: 4px;
  }

  .intensity-desc {
    font-size: 13px;
    color: var(--grey-text);
    line-height: 1.65;
  }

  /* === SPOTTING GUIDE === */
  .spot-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 20px;
  }

  .spot-card {
    border: 1px solid var(--grey-mid);
    padding: 16px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .spot-title {
    font-weight: 700;
    font-size: 13px;
    color: var(--navy);
    margin-bottom: 10px;
    padding-bottom: 6px;
    border-bottom: 2px solid var(--gold);
  }

  .spot-table {
    border-collapse: collapse;
    margin-bottom: 10px;
    font-size: 12px;
  }

  .spot-table td {
    padding: 2px 8px 2px 0;
    font-size: 12px;
  }

  .spot-key {
    color: var(--grey-text);
    font-weight: 600;
  }

  .spot-subhead {
    font-size: 10px;
    font-weight: 600;
    color: var(--grey-text);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 8px;
    margin-bottom: 4px;
  }

  .spot-phrases {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 6px;
  }

  .spot-phrase {
    display: inline-block;
    background: var(--grey-light);
    border: 1px solid var(--grey-mid);
    padding: 2px 7px;
    font-size: 11px;
  }

  .spot-behaviour {
    font-size: 12px;
    color: var(--charcoal);
    line-height: 1.5;
    margin-left: 2px;
  }

  /* === MATCHUP CARDS === */
  .matchup-card {
    border: 1px solid var(--grey-mid);
    margin-bottom: 18px;
    overflow: hidden;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .matchup-header {
    padding: 12px 20px;
    background: var(--grey-light);
    border-bottom: 1px solid var(--grey-mid);
    font-size: 13px;
  }

  .matchup-you { font-weight: 600; color: var(--navy); }
  .matchup-vs { color: var(--grey-text); margin: 0 8px; font-style: italic; }
  .matchup-them { font-weight: 600; color: var(--navy); }

  .matchup-body {
    padding: 18px 20px;
  }

  .matchup-section-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--grey-text);
    margin-bottom: 6px;
    margin-top: 14px;
  }

  .matchup-section-label:first-child { margin-top: 0; }

  .matchup-item {
    font-size: 12px;
    color: var(--charcoal);
    margin-bottom: 4px;
    line-height: 1.55;
  }

  .matchup-columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }

  .matchup-warning {
    margin-top: 14px;
    padding: 14px 16px;
    background: var(--navy);
    border-left: 3px solid var(--gold);
  }

  .matchup-warning-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--gold-light);
    margin-bottom: 5px;
  }

  .matchup-warning p {
    font-size: 12px;
    color: rgba(255,255,255,0.85);
    line-height: 1.55;
  }

  /* === IMPORTANT RULE BOX === */
  .rule-box {
    margin-top: 20px;
    padding: 20px 24px;
    background: var(--navy);
  }

  .rule-box h4 {
    font-size: 13px;
    font-weight: 600;
    color: var(--gold-light);
    margin-bottom: 8px;
  }

  .rule-box p {
    font-size: 12px;
    color: rgba(255,255,255,0.85);
    line-height: 1.65;
  }

  /* === CLOSING === */
  .closing-box {
    padding: 28px;
    background: var(--grey-light);
    border: 1px solid var(--grey-mid);
    margin-top: 28px;
  }

  .closing-box h3 {
    font-family: 'Playfair Display', serif;
    font-size: 18px;
    color: var(--navy);
    margin-bottom: 10px;
  }

  .closing-box p {
    font-size: 13px;
    line-height: 1.65;
    color: var(--grey-text);
    margin-bottom: 6px;
  }

  .closing-box p:last-child { margin-bottom: 0; }

  /* === PRINT === */
  @media print {
    body { background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

    .page {
      margin: 0;
      padding: 45px 55px 85px 55px;   /* slightly tighter but still protected bottom */
      page-break-after: always;
      page-break-inside: avoid;
      height: 297mm;                  /* force exact page height */
      max-height: 297mm;
      overflow: hidden;               /* prevent spill for now */
    }

    .page:last-child { page-break-after: auto; }

    .cover { padding: 60px 55px; }
  }

  @page {
    size: A4;
    margin: 0;
  }
</style>
</head>
<body>

<!-- PAGE 1: COVER -->
<div class="page cover">
  <div class="cover-top">
    <div class="cover-label">The Buckingham Academy</div>
    <div class="cover-confidential">Confidential</div>
  </div>
  <div class="cover-middle">
    <div class="cover-divider"></div>
    <h1>Negotiate Smarter<br>Profile Report</h1>
    <p class="subtitle">A comprehensive assessment of your negotiation style,<br>strengths, and solid strategic development recommendations.</p>
    <div class="cover-details">
      <div>
        <div class="cover-detail-label">Prepared for</div>
        <div class="cover-detail-value">${displayName}</div>
      </div>
      <div>
        <div class="cover-detail-label">Date</div>
        <div class="cover-detail-value">${today}</div>
      </div>
      <div>
        <div class="cover-detail-label">Archetype</div>
        <div class="cover-detail-value">${a.name}</div>
      </div>
      <div>
        <div class="cover-detail-label">Assessment ID</div>
        <div class="cover-detail-value">${assessmentId}</div>
      </div>
    </div>
  </div>
  <div class="cover-bottom">
    <div class="cover-brand">&copy; 2026 The Buckingham Academy Limited. All rights reserved.</div>
  </div>
</div>

<!-- PAGE 2: ARCHETYPE OVERVIEW -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>
  <div class="section-number">01 &mdash; Your Negotiation Archetype</div>
  <div class="section-title">${a.name}</div>
  <div class="section-subtitle">${a.tagline}</div>
  <div class="archetype-badges">
    <span class="archetype-badge"><span class="label">Primary</span><span class="value">${styleMeta[p].label}</span></span>
    <span class="archetype-badge"><span class="label">Secondary</span><span class="value">${styleMeta[s].label}</span></span>
  </div>
  <div class="scores">${scoreRowsHTML}</div>
  <div class="archetype-desc">
    <p>${a.narrative.replace(/\n\n/g, '</p><p>')}</p>
  </div>
  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>02</div>
  </div>
</div>

<!-- PAGE 3: PRIMARY STYLE -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>
  <div class="section-number">02 &mdash; Your Primary Style</div>
  <div class="section-title">${styleMeta[p].label}</div>
  <div class="section-subtitle">Your dominant negotiation approach &mdash; the pattern you default to under pressure.</div>
  ${primaryParts.lead ? `<div class="style-lead">${primaryParts.lead}</div>` : ''}
  ${fmtParas(primaryParts.rest || stylePrimary[p])}
  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>03</div>
  </div>
</div>

<!-- PAGE 4: SECONDARY STYLE -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>
  <div class="section-number">03 &mdash; Your Secondary Influence</div>
  <div class="section-title">${styleMeta[s].label}</div>
  <div class="section-subtitle">The supporting style that shapes how your primary approach is expressed.</div>
  ${secondaryParts.lead ? `<div class="style-lead">${secondaryParts.lead}</div>` : ''}
  ${fmtParas(secondaryParts.rest || styleSecondary[s])}
  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>04</div>
  </div>
</div>

<!-- PAGE 5: HOW OTHERS SEE YOU -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>
  <div class="section-number">04 &mdash; How Others Experience You</div>
  <div class="section-title">Your Negotiation Presence</div>
  <div class="section-subtitle">What counterparts perceive when they sit across the table from you.</div>
  ${fmtParas(a.howOthersSeeYou)}
  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>05</div>
  </div>
</div>

<!-- PAGE 6: STRENGTHS & WEAKNESSES -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>
  <div class="two-col" style="margin-top:0;">
    <div class="col-box">
      <h4>Strengths</h4>
      ${strengthItems}
    </div>
    <div class="col-box">
      <h4>Weaknesses</h4>
      ${weaknessItems}
    </div>
  </div>
  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>06</div>
  </div>
</div>

<!-- PAGE 7: BLIND SPOTS & UNDER PRESSURE -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>
  <div class="section-number">05 &mdash; Vulnerabilities</div>
  <div class="section-title">Blind Spots &amp; Pressure Points</div>
  <div class="section-subtitle">Where your natural patterns may work against you.</div>

  <div class="highlight-box">
    <h3>Your Blind Spots</h3>
    <p>${a.blindSpots.replace(/\n\n/g, '</p><p>')}</p>
  </div>

  <div class="highlight-box">
    <h3>Under Pressure</h3>
    <p>${a.underPressure.replace(/\n\n/g, '</p><p>')}</p>
  </div>

  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>07</div>
  </div>
</div>

<!-- PAGE 8: WATCH OUT + GROWTH -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>
  <div class="section-number">06 &mdash; Development</div>
  <div class="section-title">Growth Edge</div>
  <div class="section-subtitle">Your path to becoming a more complete negotiator.</div>

  <div class="highlight-box">
    <h3>⚠ Watch Out</h3>
    <p>${a.watchOut.replace(/\n\n/g, '</p><p>')}</p>
  </div>

  <div class="highlight-box">
    <h3>Your Growth Edge</h3>
    <p>${a.growthEdge.replace(/\n\n/g, '</p><p>')}</p>
  </div>

  <div style="margin-top:28px;">
    ${growthStepsHTML}
  </div>

  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>08</div>
  </div>
</div>

<!-- PAGE 9: STYLE INTENSITY (Part 1 of 2) -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>
  <div class="section-number">07 &mdash; Style Intensity Profile</div>
  <div class="section-title">Detailed Breakdown</div>
  <div class="section-subtitle">How strongly each negotiation style influences your behaviour at the table.</div>
  
  ${intensityPart1}
  
  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>09</div>
  </div>
</div>

<!-- PAGE 10: STYLE INTENSITY (Part 2 of 2) -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>
  
  ${intensityPart2}
  
  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>10</div>
  </div>
</div>

<!-- PAGE 11: READING THE ROOM - SPOTTING -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>
  <div class="section-number">08 &mdash; Reading The Room</div>
  <div class="section-title">Spot Their Style</div>
  <div class="section-subtitle">Identify your counterpart's negotiation approach within the first few minutes through their speech and behaviours.</div>
  <div class="spot-grid">
    ${spotGridHTML}
  </div>
  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>11</div>
  </div>
</div>

<!-- TACTICAL PLAYBOOK - One matchup per page (improved spacing) -->
${opponents.map((opp, index) => {
  const key = p + '-' + opp;
  const m = matchupAdvice[key];
  const oppLab = styleMeta[opp].label;
  const myLab = styleMeta[p].label;
  const pageNum = 12 + index;          // 12, 13, 14

  return `
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>

  ${index === 0 ? `
    <div class="section-number">09 &mdash; Your Tactical Playbook</div>
    <div class="section-title">Style Matchups</div>
    <div class="section-subtitle">This section helps you prepare and engage with different counterparty negotiation styles.</div>
  ` : `
    <div class="section-number">09 &mdash; Your Tactical Playbook (continued)</div>
    <div class="section-title" style="font-size:24px; margin-bottom:6px;">You (${myLab}) vs ${oppLab}</div>
    <div style="height:12px;"></div>
  `}

  <div class="matchup-card">
    <div class="matchup-header">
      <span class="matchup-you">You (${myLab})</span>
      <span class="matchup-vs">vs</span>
      <span class="matchup-them">${oppLab}</span>
    </div>

    <div class="matchup-body">

      <!-- THEIR TELLS -->
      <div class="matchup-section-label">Their Tells</div>
      <div style="height:6px;"></div>
      ${m.tells.map(t => `<p class="matchup-item">• ${t}</p>`).join('')}

      <div style="height:22px;"></div>

      <!-- YOUR ADVANTAGES -->
      <div class="matchup-section-label">Your Advantages</div>
      <div style="height:6px;"></div>
      ${m.advantages.map(av => `<p class="matchup-item">✓ ${av}</p>`).join('')}

      <div style="height:22px;"></div>

      <!-- YOUR RISKS (now stacked underneath) -->
      <div class="matchup-section-label">Your Risks</div>
      <div style="height:6px;"></div>
      ${m.risks.map(ri => `<p class="matchup-item">✗ ${ri}</p>`).join('')}

      <div style="height:22px;"></div>

      <!-- YOUR PLAYBOOK -->
      <div class="matchup-section-label">Your Playbook</div>
      <div style="height:6px;"></div>
      ${m.playbook.map((step, i) => `<p class="matchup-item"><strong>${i + 1}.</strong> ${step}</p>`).join('')}

      <div style="height:20px;"></div>

      <!-- WARNING BOX -->
      <div class="matchup-warning">
        <div class="matchup-warning-label">⚠ Watch For The Fake Style</div>
        <p>${m.fakeWarning}</p>
      </div>

    </div>
  </div>


  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>${pageNum}</div>
  </div>
</div>`;
}).join('')}

<!-- PAGE 15: SHADOW ASSESSMENT -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>

  <div class="section-number">10 &mdash; Shadow Assessment</div>
  <div class="section-title">${sl.title}</div>
  <div class="section-subtitle">${sl.sub}</div>

  <div class="shadow-meter">
    <div class="shadow-meter-label">Shadow Score</div>
    <div class="shadow-dots">${shadowDotsHTML}</div>
    <div class="shadow-score-text">${r.shadow} / 5</div>
  </div>

  ${fmtParas(sl.msg)}

  <!-- Spacer to push the box down -->
  <div style="height: 40px;"></div>

  <!-- Remember box at the bottom of page 15 -->
  <div class="rule-box">
    <h4>Remember</h4>
    <p>The most dangerous negotiator is not the one who is aggressive. It is the one who is pretending to be something they are not. If someone's words say collaboration but their proposals say competition, trust the proposals. Behaviour reveals intention far more reliably than language ever will.</p>
  </div>

  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>15</div>
  </div>
</div>

<!-- PAGE 16: CLOSING -->
<div class="page">
  <div class="page-header">
    <div class="page-header-brand">The Buckingham Academy</div>
    <div class="page-header-section">Negotiate Smarter Profile Report</div>
  </div>

  <div class="section-number">11 &mdash; What Comes Next</div>
  <div class="section-title">Using This Report</div>
  <div class="section-subtitle">Your profile is a starting point, not a label.</div>

  <p class="body-text">This report maps your current negotiation tendencies based on how you responded under simulated pressure. It reflects your defaults &mdash; the patterns you fall into when you have not consciously chosen a different approach. Defaults are not destiny. They are simply what happens when you are not paying attention.</p>
  <p class="body-text">The most effective negotiators are not locked into one style. They understand their natural inclinations, recognise when those inclinations serve them, and develop the ability to shift when circumstances demand something different.</p>
  <p class="body-text">Review your strengths to understand where you already operate effectively. Study your blind spots to identify where you may be leaving value on the table. Use the tactical playbook to prepare for specific counterpart styles. And pay attention to your shadow score &mdash; it tells you something important about the gap between who you present yourself as and how you actually operate.</p>

  <div class="closing-box">
    <h3>The Buckingham Academy</h3>
    <p>We coach teams to close deals. If you want to deepen your negotiation capability &mdash; whether through one-to-one coaching, team workshops, or advanced scenario training &mdash; we would be glad to hear from you.</p>
    <p><strong>Email:</strong> admin@bucademy.com</p>
    <p><strong>Web:</strong> www.buckingham.academy</p>
  </div>

  <div class="page-footer">
    <div>Prepared exclusively for ${displayName}</div>
    <div>Confidential</div>
    <div>16</div>
  </div>
</div>

</body>
</html>`;
}

const PetalChart = ({ scores }) => {
  const cx = 200, cy = 200;
  const maxR = 125;
  const ringOuterR = 178;
  const ringInnerR = 128;
  const total = 16;

  const styles = [
    { key:'dominator', label:'DOMINATOR', color:'#DC2626', light:'#FEE2E2', angle:-Math.PI/2 },
    { key:'integrator', label:'INTEGRATOR', color:'#9333EA', light:'#F3E8FF', angle:0 },
    { key:'yielder', label:'YIELDER', color:'#16A34A', light:'#DCFCE7', angle:Math.PI/2 },
    { key:'calculator', label:'CALCULATOR', color:'#2563EB', light:'#DBEAFE', angle:Math.PI },
  ];

  const arcPath = (startAngle, endAngle, iR, oR) => {
    const sx = cx + oR * Math.cos(startAngle), sy = cy + oR * Math.sin(startAngle);
    const ex = cx + oR * Math.cos(endAngle), ey = cy + oR * Math.sin(endAngle);
    const ix = cx + iR * Math.cos(endAngle), iy = cy + iR * Math.sin(endAngle);
    const jx = cx + iR * Math.cos(startAngle), jy = cy + iR * Math.sin(startAngle);
    return `M${sx},${sy} A${oR},${oR} 0 0 1 ${ex},${ey} L${ix},${iy} A${iR},${iR} 0 0 0 ${jx},${jy}Z`;
  };

  const wedgePath = (startAngle, endAngle, r) => {
    const sx = cx + r * Math.cos(startAngle), sy = cy + r * Math.sin(startAngle);
    const ex = cx + r * Math.cos(endAngle), ey = cy + r * Math.sin(endAngle);
    return `M${cx},${cy} L${sx},${sy} A${r},${r} 0 0 1 ${ex},${ey} Z`;
  };

  return (
   <svg viewBox="-30 -30 460 460" className="w-full h-full">
      {/* Background */}
      <circle cx={cx} cy={cy} r={ringOuterR + 2} fill="#F8FAFC"/>

      {/* Light quadrant tints */}
      {styles.map(s => (
        <path key={`bg-${s.key}`}
          d={wedgePath(s.angle - Math.PI/4, s.angle + Math.PI/4, ringInnerR)}
          fill={s.light} opacity="0.35"/>
      ))}

      {/* Guide rings */}
      {[0.25, 0.5, 0.75, 1].map(f => (
        <circle key={f} cx={cx} cy={cy} r={maxR * f}
          fill="none" stroke="#E2E8F0" strokeWidth="0.75"/>
      ))}

      {/* Solid coloured score wedges */}
      {styles.map(s => {
        const r = Math.max((scores[s.key] / total) * maxR, 8);
        return (
          <path key={`score-${s.key}`}
            d={wedgePath(s.angle - Math.PI/4, s.angle + Math.PI/4, r)}
            fill={s.color} opacity="1"/>
        );
      })}

      {/* White dividers between quadrants */}
      {styles.map(s => {
        const a = s.angle - Math.PI / 4;
        return (
          <line key={`div-${s.key}`} x1={cx} y1={cy}
            x2={cx + ringInnerR * Math.cos(a)} y2={cy + ringInnerR * Math.sin(a)}
            stroke="white" strokeWidth="3" opacity="0.9"/>
        );
      })}

      {/* Centre dot */}
      <circle cx={cx} cy={cy} r="7" fill="white" stroke="#E5E7EB" strokeWidth="1.5"/>
      <circle cx={cx} cy={cy} r="3" fill="#94A3B8" opacity="0.4"/>

      {/* Outer ring arcs */}
      {styles.map(s => (
        <path key={`ring-${s.key}`}
          d={arcPath(s.angle - Math.PI/4, s.angle + Math.PI/4, ringInnerR, ringOuterR)}
          fill={s.color} stroke={s.color} strokeWidth="0.5"/>
      ))}

      {/* White separators on ring */}
      {styles.map(s => {
        const a = s.angle - Math.PI / 4;
        return (
          <line key={`rsep-${s.key}`}
            x1={cx + ringInnerR * Math.cos(a)} y1={cy + ringInnerR * Math.sin(a)}
            x2={cx + ringOuterR * Math.cos(a)} y2={cy + ringOuterR * Math.sin(a)}
            stroke="white" strokeWidth="3"/>
        );
      })}

      {/* Curved labels on ring */}
      {styles.map(s => {
        const r = (ringOuterR + ringInnerR) / 2;
        const spread = Math.PI / 5;
        let pathD;
        if (s.angle === Math.PI || s.angle === Math.PI/2) {
          const sa = s.angle + spread, ea = s.angle - spread;
          pathD = `M${cx + r * Math.cos(sa)},${cy + r * Math.sin(sa)} A${r},${r} 0 0 0 ${cx + r * Math.cos(ea)},${cy + r * Math.sin(ea)}`;
        } else {
          const sa = s.angle - spread, ea = s.angle + spread;
          pathD = `M${cx + r * Math.cos(sa)},${cy + r * Math.sin(sa)} A${r},${r} 0 0 1 ${cx + r * Math.cos(ea)},${cy + r * Math.sin(ea)}`;
        }
        return (
          <g key={`lbl-${s.key}`}>
            <path id={`tp2-${s.key}`} d={pathD} fill="none"/>
            <text>
              <textPath href={`#tp2-${s.key}`} startOffset="50%" textAnchor="middle"
               fill="white" fontSize="16" fontWeight="bold" fontFamily="system-ui,sans-serif"
                letterSpacing="4">{s.label}</textPath>
            </text>
          </g>
        );
      })}

      {/* Score percentages */}
      {styles.map(s => {
        const lR = ringOuterR + 20;
        const lx = cx + lR * Math.cos(s.angle);
        const ly = cy + lR * Math.sin(s.angle);
        const pct = Math.round((scores[s.key] / total) * 100);
        return (
          <text key={`score-${s.key}`} x={lx} y={ly + 4} textAnchor="middle"
            fill={s.color} fontSize="14" fontWeight="bold" fontFamily="system-ui,sans-serif">
            {pct}%
          </text>
        );
      })}
    </svg>
  );
};

export default function NegotiationAssessment(){
  const[phase,setPhase]=useState('intro');
  const[qi,setQi]=useState(0);
  const[answers,setAnswers]=useState([]);
  const[sel,setSel]=useState(null);
  const[results,setResults]=useState(null);
  const[userName,setUserName]=useState('');
  const[tieData,setTieData]=useState(null); 
  const[saved,setSaved]=useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailStatus, setEmailStatus] = useState('idle');
  const [accessCode, setAccessCode] = useState('');
  const [accessError, setAccessError] = useState('');
  const [codeSource, setCodeSource] = useState('');    // ← ADD THIS LINE
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const back = () => {
    if (qi > 0) {
      setQi(qi - 1);
      setSel(answers[qi - 1] ?? null);
    }
  };

const next = () => {
  if (sel === null) return;
  const na = [...answers];
  na[qi] = sel;
  setAnswers(na);
  if (qi === questions.length - 1) {
    const r = calcResults(na);
    if (r.tied) { setTieData(r); setPhase('tiebreak'); }
    else { setResults(r); setPhase('results'); }
  } else {
    setQi(qi + 1);
    setSel(na[qi + 1] ?? null);
  }
};

const download = () => {
  const html = genHTML(results, userName);
  const win = window.open('', '_blank');
  if (!win) {
    alert('Please allow pop-ups to download your report.');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    setTimeout(() => win.print(), 300);
  };
};

useEffect(() => {
  if (phase === 'results' && results && !saved) {
    setSaved(true);
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  body: new URLSearchParams({
'form-name': 'completions',
'name': userName || 'Anonymous',
'email': userEmail || '',
'archetype': results.archetype.name,
'style': results.primary,
'codeSource': codeSource          // ← ADD THIS
}).toString()
    }).catch(err => console.error('Form submit failed:', err));
  }
}, [phase, results, userName, saved, codeSource]);  // ← ADD codeSource here too


const sendReportEmail = async () => {
  if (!results || !userEmail) return;
  setEmailStatus('sending');
  try {
    const response = await fetch('/api/send-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: userEmail.trim(),
        userName: userName || '',
        scores: results.scores,
        shadow: results.shadow,
        primary: results.primary,
        secondary: results.secondary,
        archetype: results.archetype,
        primaryDesc: stylePrimary[results.primary],
        secondaryDesc: styleSecondary[results.secondary],
        shadowLevel: shadowLevels[results.shadow],
        styleMeta: Object.fromEntries(
          Object.entries(styleMeta).map(([k, v]) => [k, { label: v.label, color: v.color }])
        ),
      }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to send');
    setEmailStatus('sent');
  } catch (err) {
    console.error('Failed to send report email:', err);
    setEmailStatus('failed');
  }
};

if (phase === 'intro') return (
  <div className="min-h-screen bg-slate-950">
<WhatsAppButton />

    {/* ═══ HERO ═══ */}
    <div id="start" className="relative bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-start justify-center px-6 overflow-hidden pt-12 pb-20">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      <motion.div 
        initial={{opacity:0, y:30}} 
        animate={{opacity:1, y:0}} 
        transition={{duration:1}} 
        className="relative z-10 max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
      >
       {/* Left - Hero text */}
<div className="text-center lg:text-left">
  <p className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-8">
    The Buckingham Academy
  </p>

  <h1 className="text-4xl sm:text-5xl font-bold mb-3 leading-tight tracking-tight text-white">
    You're Leaving Value<br/>on the Table.
  </h1>

  <p className="text-3xl sm:text-4xl font-bold mb-6 leading-tight tracking-tight bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">
    Let's Fix That.
  </p>

  <p className="text-slate-300 text-lg sm:text-xl mb-8 max-w-lg leading-relaxed mx-auto lg:mx-0">
    Take the 7-minute Negotiation Style Assessment and uncover your hidden patterns affecting every deal outcome.
  </p>

  {/* ═══ NEW BULLET POINTS ═══ */}
  <ul className="space-y-3 mb-12 max-w-lg mx-auto lg:mx-0 text-left">
    {[
      "A personalised diagnostic on your current negotiation style",
      "Insights on where you excel and where you can improve",
      "Solid recommendations to improve your deal outcomes"
    ].map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="text-amber-400 text-xl leading-none mt-0.5">⚡</span>
        <span className="text-slate-300 text-base sm:text-lg leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>

  {/* Stats row */}
  <div className="flex items-center justify-center lg:justify-start gap-6 sm:gap-8 text-xs">
    {[['21','Questions'],['4','Styles'],['12','Archetypes'],['7','Minutes']].map(([num,label],i)=>(
      <div key={i} className="flex flex-col items-center gap-1">
        <span className="text-2xl font-bold text-white">{num}</span>
        <span className="uppercase tracking-widest text-slate-400 text-xs">{label}</span>
        
      </div>
    ))}
  </div>
</div>

{/* Right - Form card */}
<div className="bg-white rounded-2xl shadow-2xl p-8 sm:p-10">

{!showRequestForm ? (
    <>
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Start here</h2>
      <p className="text-slate-500 text-base mb-8">Enter your details to begin.</p>

      <div className="mb-5 text-left">
        <label className="block text-sm text-slate-700 mb-2 uppercase tracking-widest font-bold">First Name</label>
        <input
          type="text"
          value={userName}
          onChange={e => setUserName(e.target.value)}
          placeholder="e.g. Alex"
          className="w-full px-5 py-4 border border-gray-200 rounded-lg text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all text-base"
        />
      </div>

      <div className="mb-5 text-left">
        <label className="block text-sm text-slate-700 mb-2 uppercase tracking-widest font-bold">Email</label>
        <input
          type="email"
          value={userEmail}
          onChange={e => { setUserEmail(e.target.value); setEmailError(''); }}
          placeholder="you@email.com"
          className={`w-full px-5 py-4 border rounded-lg text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all text-base ${emailError ? 'border-red-400' : 'border-gray-200'}`}
        />
        {emailError && <p className="text-red-500 text-xs mt-2">{emailError}</p>}
      </div>

      <div className="mb-4 text-left">
        <label className="block text-sm text-slate-700 mb-2 uppercase tracking-widest font-bold">Access Code</label>
        <input
          type="text"
          value={accessCode}
          onChange={e => { setAccessCode(e.target.value); setAccessError(''); }}
          placeholder="Enter your invite code"
          className={`w-full px-5 py-4 border rounded-lg text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all text-base ${accessError ? 'border-red-400' : 'border-gray-200'}`}
        />
        {accessError && <p className="text-red-500 text-xs mt-2">{accessError}</p>}
      </div>

      <p className="text-sm text-slate-500 mb-8 text-left">
        Don't have a code?{' '}
        <button
          type="button"
          onClick={() => setShowRequestForm(true)}
          className="text-blue-600 hover:underline font-medium"
        >
          Request access →
        </button>
      </p>

<button
  onClick={() => {
    if (!userName.trim()) { setEmailError('Please enter your first name'); return; }
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail.trim());
    if (!valid) { setEmailError('Please enter a valid email address'); return; }

    const code = accessCode.trim().toUpperCase();
    const validCodes = {
      'NEGOTIATOR2026': 'Denis',      // Your personal code
      'NEGOTIATE2027':  'Terence',   // Sales team code
    };

    if (!validCodes[code]) { setAccessError('Invalid access code'); return; }

    setCodeSource(validCodes[code]);  // Store which channel they came from
    setPhase('quiz');
  }}
        className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold px-8 py-4 rounded-xl text-lg transition-all shadow-lg hover:shadow-xl"
      >
        Begin Assessment
      </button>
    </>
  ) : !requestSubmitted ? (
    <>
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Request Access</h2>
      <p className="text-slate-500 text-base mb-8">A few details and we'll get you set up. Each assessment costs US$19. Participants in our Negotiation Training receive free access.</p>

      <form
        name="access-request"
        method="POST"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        onSubmit={async (e) => {
          e.preventDefault();
          const body = new URLSearchParams({
            'form-name': 'access-request',
            'name': e.target.querySelector('[name="name"]').value,
            'email': e.target.querySelector('[name="email"]').value,
            'reason': e.target.querySelector('[name="reason"]').value,
          }).toString();
          try {
            const response = await fetch('/', {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: body,
            });
            console.log('Response status:', response.status);
            if (response.ok) {
              setRequestSubmitted(true);
            }
          } catch (error) {
            console.error('Form submission error:', error);
          }
        }}
      >
        <input type="hidden" name="form-name" value="access-request" />
        <p className="hidden">
          <label>Don't fill this out: <input name="bot-field" /></label>
        </p>

        <div className="mb-5 text-left">
          <label className="block text-sm text-slate-700 mb-2 uppercase tracking-widest font-bold">Name</label>
          <input
            type="text"
            name="name"
            required
            placeholder="First name only"
            className="w-full px-5 py-4 border border-gray-200 rounded-lg text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all text-base"
          />
        </div>

        <div className="mb-5 text-left">
          <label className="block text-sm text-slate-700 mb-2 uppercase tracking-widest font-bold">Email</label>
          <input
            type="email"
            name="email"
            required
            placeholder="you@email.com"
            className="w-full px-5 py-4 border border-gray-200 rounded-lg text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all text-base"
          />
        </div>

        <div className="mb-8 text-left">
          <label className="block text-sm text-slate-700 mb-2 uppercase tracking-widest font-bold">Are you participating in our Negotiation Training? YES/NO</label>
          <textarea
            name="reason"
            rows="3"
            placeholder="YES I am / NO not yet"
            className="w-full px-5 py-4 border border-gray-200 rounded-lg text-gray-800 bg-gray-50 focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all text-base resize-none"
          />
        </div>

        <button
          type="submit"
          className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold px-8 py-4 rounded-xl text-lg transition-all shadow-lg hover:shadow-xl"
        >
          Submit Request
        </button>
      </form>

      <button
        type="button"
        onClick={() => setShowRequestForm(false)}
        className="w-full mt-4 text-slate-500 text-sm hover:text-slate-700 transition-all"
      >
        ← Back to sign in
      </button>
    </>
  ) : (
    <div className="text-center py-10">
      <div className="text-5xl mb-4">✓</div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Request Submitted</h2>
      <p className="text-slate-500 mb-8">We'll email you back with a Access Code or Stripe payment link.</p>
      <button
        type="button"
        onClick={() => { setShowRequestForm(false); setRequestSubmitted(false); }}
        className="text-blue-600 hover:underline font-medium text-sm"
      >
        ← Back to sign in
      </button>
    </div>
  )}

</div>
 </motion.div>
    </div>

    {/* ═══ PROBLEM ═══ */}
    <div className="bg-slate-950 px-6 py-24">
      <motion.div 
        initial={{opacity:0, y:30}} 
        whileInView={{opacity:1, y:0}} 
        viewport={{once:true, margin:'-100px'}} 
        transition={{duration:0.8}} 
        className="max-w-2xl mx-auto text-center"
      >
        <p className="text-red-400 text-sm font-semibold tracking-widest uppercase mb-6">The Problem</p>
        <h2 className="text-3xl sm:text-4xl font-bold mb-8 leading-tight text-white">
          Most Professionals Negotiate<br/>on Autopilot
        </h2>
        
        <div className="space-y-5 text-slate-300 text-lg leading-relaxed max-w-xl mx-auto">
          <p>
            You've developed habits over years, the ways you handle pushback, make concessions, build (or avoid) tension. 
            Most of it is <span className="text-white font-medium">invisible to you</span>.
          </p>
          <p>
            Some of those instincts give you a genuine edge. Others are <span className="text-white font-medium">quietly costing you</span> in margin, in trust, in outcomes you never realise you missed.
          </p>
          <p className="text-white font-medium text-xl pt-4">
            You can't fix what you can't see.
          </p>
        </div>

        <div className="flex justify-center mt-10">
          <button
            onClick={() => document.getElementById('start')?.scrollIntoView({behavior:'smooth'})}
            className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-10 py-3.5 rounded-xl text-base transition-all shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5 flex items-center gap-1"
          >
            Take The Assessment <ChevronRight className="w-5 h-5" />
          </button>
</div>
      </motion.div>
    </div>

    {/* ═══ 4 STYLES ═══ */}
    <div className="bg-gradient-to-b from-slate-950 to-slate-900 px-6 py-24">
      <motion.div 
        initial={{opacity:0, y:30}} 
        whileInView={{opacity:1, y:0}} 
        viewport={{once:true, margin:'-100px'}} 
        transition={{duration:0.8}} 
        className="max-w-3xl mx-auto text-center"
      >
        <p className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-6">Four Distinct Styles</p>
        <h2 className="text-3xl sm:text-4xl font-bold mb-4 leading-tight text-white">Which One Drives You?</h2>
        <p className="text-slate-300 mb-14 text-lg max-w-md mx-auto">
          Everyone leans towards one dominant style, often without knowing it.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
          {[
            {name:'Dominator', bg:'from-red-600 to-red-700', border:'border-red-500/40', desc:'Drives hard for results. Decisive under pressure, but may leave value on the table by moving too fast.', icon:'⚡'},
            {name:'Integrator', bg:'from-purple-600 to-purple-700', border:'border-purple-500/40', desc:'Builds relationships and consensus. Creates trust, but may concede too much to keep the peace.', icon:'🤝'},
            {name:'Yielder', bg:'from-emerald-600 to-emerald-700', border:'border-emerald-500/40', desc:'Accommodating and patient. Keeps doors open, but risks being overlooked or outmanoeuvred.', icon:'🌿'},
            {name:'Calculator', bg:'from-blue-600 to-blue-700', border:'border-blue-500/40', desc:'Analytical and methodical. Masters the detail, but may delay momentum or miss the human dynamic.', icon:'📐'},
          ].map((s,i)=>(
            <motion.div 
              key={s.name} 
              initial={{opacity:0, y:20}} 
              whileInView={{opacity:1, y:0}} 
              viewport={{once:true}} 
              transition={{delay:i*0.1, duration:0.6}}
              className={`bg-slate-800/60 ${s.border} border rounded-2xl p-6 text-left hover:bg-slate-800/90 transition-all duration-300`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`bg-gradient-to-r ${s.bg} w-10 h-10 rounded-lg flex items-center justify-center text-lg shadow-lg`}>
                  {s.icon}
                </div>
                <h3 className="text-white font-bold text-lg">{s.name}</h3>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        <p className="text-slate-400 text-sm mt-10 italic">
          Your style is only the starting point; your unique archetype gives you deeper knowledge.
        </p>
      </motion.div>
    </div>

    {/* ═══ BENEFITS ═══ */}
    <div className="bg-slate-900 px-6 py-24">
      <motion.div 
        initial={{opacity:0, y:30}} 
        whileInView={{opacity:1, y:0}} 
        viewport={{once:true, margin:'-100px'}} 
        transition={{duration:0.8}} 
        className="max-w-3xl mx-auto text-center"
      >
        <p className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-6">Your Personalised Diagnostic</p>
        <h2 className="text-3xl sm:text-4xl font-bold mb-4 leading-tight text-white">
          This Isn't a Quiz.<br/>It's a Strategic Mirror.
        </h2>
        <p className="text-slate-300 text-lg mb-16 max-w-lg mx-auto">
          In 7 minutes you'll receive a detailed profile that most negotiators never get access to, giving you Deal Advantage.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-left max-w-2xl mx-auto">
          {[
            {title:'Your Negotiation DNA', desc:'Understand your dominant style, your secondary tendencies, and the archetype that makes you unique among the 12 profiles.', icon:'🧬'},
            {title:'Your Natural Edge', desc:"See exactly where your instincts serve you, the advantages most people in your style never consciously leverage.", icon:'🎯'},
            {title:'Your Blind Spots', desc:'Uncover the patterns that quietly erode trust, margin, or momentum before your next high-stakes conversation.', icon:'🔍'},
            {title:'How to Read Others', desc:'Learn to identify each style across the table and adapt your approach in real time to create better outcomes for everyone.', icon:'🧭'},
          ].map((b,i)=>(
            <motion.div 
              key={b.title} 
              initial={{opacity:0, y:20}} 
              whileInView={{opacity:1, y:0}} 
              viewport={{once:true}} 
              transition={{delay:i*0.1, duration:0.6}}
              className="bg-slate-800/50 border border-slate-700/60 rounded-2xl p-6 hover:border-blue-500/40 hover:bg-slate-800/80 transition-all duration-300"
            >
              <div className="text-2xl mb-3">{b.icon}</div>
              <h3 className="text-white font-bold text-base mb-2">{b.title}</h3>
              <p className="text-slate-300 text-sm leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>

{/* ═══ RESULTS PREVIEW / TEASER ═══ */}
    <div className="bg-gradient-to-b from-slate-900 to-slate-950 px-6 py-24 overflow-hidden">
      <style>{`
        @keyframes pdfScroll {
          0%, 12% { transform: translateY(0); }
          22%, 34% { transform: translateY(-580px); }
          44%, 56% { transform: translateY(-1160px); }
          66%, 78% { transform: translateY(-1740px); }
          88%, 100% { transform: translateY(0); }
        }
      `}</style>
      <motion.div 
        initial={{opacity:0, y:30}} 
        whileInView={{opacity:1, y:0}} 
        viewport={{once:true, margin:'-100px'}} 
        transition={{duration:0.8}} 
        className="max-w-3xl mx-auto text-center"
      >
        <p className="text-blue-400 text-xs font-semibold tracking-widest uppercase mb-6">What You'll Receive</p>
        <h2 className="text-3xl sm:text-4xl font-bold mb-4 leading-tight text-white">
          Your 16-Page Profile Report
        </h2>
        <p className="text-slate-400 text-sm sm:text-base mb-12 max-w-lg mx-auto">
          A personalised deep-dive into your negotiation style, strengths, blind spots, and tactical playbook.
        </p>

        {/* 3D PDF Preview */}
        <div style={{perspective:'1200px'}} className="flex justify-center mb-14">
          <div 
            className="relative overflow-hidden rounded-lg"
            style={{
              width:'100%',
              maxWidth:'460px',
              height:'580px',
              transform:'rotateY(-4deg) rotateX(2deg)',
              boxShadow:'0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
            }}
          >
            {/* Scrolling pages */}
            <div style={{animation:'pdfScroll 28s ease-in-out infinite'}}>

              {/* ── COVER PAGE ── */}
              <div style={{
                height:'580px',
                background:'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
                padding:'32px',
                display:'flex',
                flexDirection:'column',
              }}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <span style={{color:'#c8a45e', fontSize:'8px', fontWeight:700, letterSpacing:'2.5px'}}>THE BUCKINGHAM ACADEMY</span>
                  <span style={{color:'#64748b', fontSize:'7px', border:'1px solid #475569', padding:'3px 8px', letterSpacing:'1.5px'}}>CONFIDENTIAL</span>
                </div>
                <div style={{marginTop:'auto', marginBottom:'auto'}}>
                  <div style={{width:'40px', height:'3px', background:'#c8a45e', marginBottom:'28px'}}/>
                  <h3 style={{fontFamily:'Georgia, "Times New Roman", serif', fontSize:'32px', color:'white', fontWeight:'bold', lineHeight:1.15, marginBottom:'12px', textAlign:'left'}}>
                    Negotiate Smarter<br/>Profile Report
                  </h3>
                  <p style={{color:'#94a3b8', fontSize:'11px', textAlign:'left', lineHeight:1.7, marginBottom:'36px'}}>
                    A comprehensive assessment of your negotiation style,<br/>strengths, and solid strategic development recommendations.
                  </p>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'18px', textAlign:'left'}}>
                    {[
                      {label:'PREPARED FOR', value:'Your Name', color:'#fff'},
                      {label:'DATE', value:'21 September 2026', color:'#fff'},
                      {label:'ARCHETYPE', value:'Unique To You', color:'#60a5fa'},
                      {label:'ASSESSMENT ID', value:'NP-2026-XXXXX', color:'#fff'},
                    ].map(m=>(
                      <div key={m.label}>
                        <p style={{color:'#c8a45e', fontSize:'7px', letterSpacing:'2px', marginBottom:'4px'}}>{m.label}</p>
                        <p style={{color:m.color, fontSize:'12px'}}>{m.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <p style={{color:'#475569', fontSize:'8px', marginTop:'auto'}}>© 2026 The Buckingham Academy Limited. All rights reserved.</p>
              </div>

  {/* ── PAGE 2 — ARCHETYPE ── */}
              <div style={{height:'580px', background:'white', padding:'28px 32px', display:'flex', flexDirection:'column'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom:'12px', borderBottom:'1px solid #e2e8f0', marginBottom:'20px'}}>
                  <span style={{color:'#64748b', fontSize:'7px', letterSpacing:'2px'}}>THE BUCKINGHAM ACADEMY</span>
                  <span style={{color:'#94a3b8', fontSize:'7px', letterSpacing:'1.5px'}}>NEGOTIATE SMARTER PROFILE REPORT</span>
                </div>
                <p style={{color:'#c8a45e', fontSize:'9px', fontWeight:700, letterSpacing:'2px', marginBottom:'6px'}}>01 — YOUR NEGOTIATION ARCHETYPE</p>
                <h3 style={{fontFamily:'Georgia, "Times New Roman", serif', fontSize:'28px', color:'#1e293b', fontWeight:'bold', marginBottom:'6px', textAlign:'left'}}>The Oracle</h3>
                <p style={{color:'#64748b', fontSize:'11px', textAlign:'left', marginBottom:'16px', fontStyle:'italic'}}>You see everything. Now act on it.</p>
                <div style={{display:'flex', gap:'8px', marginBottom:'18px'}}>
                  <span style={{border:'1px solid #e2e8f0', padding:'5px 12px', fontSize:'10px', color:'#1e293b'}}><span style={{color:'#94a3b8'}}>PRIMARY </span><strong>Calculator</strong></span>
                  <span style={{border:'1px solid #e2e8f0', padding:'5px 12px', fontSize:'10px', color:'#1e293b'}}><span style={{color:'#94a3b8'}}>SECONDARY </span><strong>Yielder</strong></span>
                </div>
                {[
                  {label:'Calculator', pct:38, lvl:'HIGH'},
                  {label:'Yielder', pct:31, lvl:'MODERATE'},
                  {label:'Integrator', pct:19, lvl:'LOW'},
                  {label:'Dominator', pct:13, lvl:'LOW'},
                ].map(bar=>(
                  <div key={bar.label} style={{marginBottom:'14px'}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'5px'}}>
                      <span style={{color:'#1e293b', fontSize:'11px', fontWeight:600}}>{bar.label}</span>
                      <div style={{textAlign:'right'}}>
                        <span style={{color:'#1e293b', fontSize:'14px', fontWeight:700}}>{bar.pct}%</span>
                        <span style={{color:'#94a3b8', fontSize:'8px', letterSpacing:'1px', marginLeft:'6px'}}>{bar.lvl}</span>
                      </div>
                    </div>
                    <div style={{width:'100%', background:'#f1f5f9', borderRadius:'2px', height:'5px'}}>
                      <div style={{width:`${bar.pct}%`, background:'#334155', borderRadius:'2px', height:'5px'}}/>
                    </div>
                  </div>
                ))}
                <div style={{borderLeft:'3px solid #c8a45e', paddingLeft:'16px', marginTop:'12px'}}>
                  <p style={{color:'#475569', fontSize:'10px', lineHeight:1.7, textAlign:'left'}}>
                    The Oracle has the deepest insight of any profile. You combine the Calculator's analytical power with the Yielder's emotional intelligence, giving you a complete understanding of both the data and the people involved...
                  </p>
                </div>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'auto', paddingTop:'12px', borderTop:'1px solid #f1f5f9'}}>
                  <span style={{color:'#94a3b8', fontSize:'8px', fontStyle:'italic'}}>Prepared exclusively for Your Name</span>
                  <span style={{color:'#94a3b8', fontSize:'8px'}}>02</span>
                </div>
              </div>

              {/* ── PAGE 3 — STRENGTHS & WEAKNESSES ── */}
              <div style={{height:'580px', background:'white', padding:'28px 32px', display:'flex', flexDirection:'column'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom:'12px', borderBottom:'1px solid #e2e8f0', marginBottom:'20px'}}>
                  <span style={{color:'#64748b', fontSize:'7px', letterSpacing:'2px'}}>THE BUCKINGHAM ACADEMY</span>
                  <span style={{color:'#94a3b8', fontSize:'7px', letterSpacing:'1.5px'}}>NEGOTIATE SMARTER PROFILE REPORT</span>
                </div>
                <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'24px', flex:1}}>
                  <div>
                    <p style={{color:'#1e293b', fontSize:'10px', fontWeight:700, letterSpacing:'2px', marginBottom:'16px'}}>STRENGTHS</p>
                    {[
                      'Deeply perceptive in ways that consistently reveal things others miss entirely.',
                      'You understand people and situations at a level most negotiators never reach.',
                      'Your combination of analytical and emotional intelligence means you see the complete picture.',
                      'Your insight is genuinely rare, and when you share it, it changes how people think about the problem.',
                      'You are the advisor everyone needs and few know to ask for.',
                    ].map((s,i)=>(
                      <div key={i} style={{display:'flex', gap:'6px', marginBottom:'10px', alignItems:'flex-start'}}>
                        <span style={{color:'#475569', fontSize:'11px', marginTop:'1px'}}>✓</span>
                        <p style={{color:'#475569', fontSize:'9px', lineHeight:1.65, textAlign:'left'}}>{s}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p style={{color:'#1e293b', fontSize:'10px', fontWeight:700, letterSpacing:'2px', marginBottom:'16px'}}>WEAKNESSES</p>
                    {[
                      'You observe and advise but struggle to lead, preferring to support rather than take the reins.',
                      'Your exceptional insight often goes unused or gets filtered through less capable people.',
                      'You have all the information needed to be outstanding but cannot bring yourself to sit at the head of it.',
                      'This is not humility, despite how it might feel from the inside.',
                      'It is a limitation that costs you influence and outcomes.',
                    ].map((s,i)=>(
                      <div key={i} style={{display:'flex', gap:'6px', marginBottom:'10px', alignItems:'flex-start'}}>
                        <span style={{color:'#94a3b8', fontSize:'11px', marginTop:'1px'}}>✗</span>
                        <p style={{color:'#475569', fontSize:'9px', lineHeight:1.65, textAlign:'left'}}>{s}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'12px', borderTop:'1px solid #f1f5f9'}}>
                  <span style={{color:'#94a3b8', fontSize:'8px', fontStyle:'italic'}}>Prepared exclusively for Your Name</span>
                  <span style={{color:'#94a3b8', fontSize:'8px'}}>06</span>
                </div>
              </div>

              {/* ── PAGE 4 — TACTICAL PLAYBOOK ── */}
              <div style={{height:'580px', background:'white', padding:'28px 32px', display:'flex', flexDirection:'column'}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom:'12px', borderBottom:'1px solid #e2e8f0', marginBottom:'20px'}}>
                  <span style={{color:'#64748b', fontSize:'7px', letterSpacing:'2px'}}>THE BUCKINGHAM ACADEMY</span>
                  <span style={{color:'#94a3b8', fontSize:'7px', letterSpacing:'1.5px'}}>NEGOTIATE SMARTER PROFILE REPORT</span>
                </div>
                <p style={{color:'#c8a45e', fontSize:'9px', fontWeight:700, letterSpacing:'2px', marginBottom:'6px'}}>09 — YOUR TACTICAL PLAYBOOK</p>
                <h3 style={{fontFamily:'Georgia, "Times New Roman", serif', fontSize:'24px', color:'#1e293b', fontWeight:'bold', marginBottom:'6px', textAlign:'left'}}>Style Matchups</h3>
                <p style={{color:'#64748b', fontSize:'10px', textAlign:'left', marginBottom:'16px'}}>How to prepare for and engage with different counterpart negotiation styles.</p>
                <div style={{border:'1px solid #e2e8f0', borderRadius:'8px', padding:'18px', marginBottom:'14px'}}>
                <p style={{color:'#1e293b', fontSize:'11px', marginBottom:'14px'}}><strong>You (Calculator)</strong> <span style={{color:'#94a3b8', fontStyle:'italic', margin:'0 4px'}}>vs</span> <strong>Dominator</strong></p>                  <p style={{color:'#94a3b8', fontSize:'8px', letterSpacing:'1.5px', marginBottom:'8px'}}>YOUR PLAYBOOK</p>
                  {[
                    'Present your strongest data point early to establish credibility and earn their respect',
                    'Match their decisiveness with confident positions backed by solid evidence',
                    'When they apply pressure, respond with facts not emotions. They respect substance over style',
                  ].map((s,i)=>(
                    <p key={i} style={{color:'#475569', fontSize:'9px', lineHeight:1.6, textAlign:'left', marginBottom:'5px'}}>
                      <strong style={{color:'#1e293b'}}>{i+1}.</strong> {s}
                    </p>
                  ))}
                  <div style={{background:'#fffbeb', borderRadius:'6px', padding:'12px', marginTop:'14px'}}>
                    <p style={{color:'#92400e', fontSize:'8px', fontWeight:700, letterSpacing:'1.5px', marginBottom:'4px'}}>⚠ WATCH FOR THE FAKE STYLE</p>
                    <p style={{color:'#78716c', fontSize:'9px', lineHeight:1.6, textAlign:'left'}}>
                      If their aggression spikes after you present strong evidence, they are likely bluffing because your data has weakened their position. Hold firm and let the silence work.
                    </p>
                  </div>
                </div>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'auto', paddingTop:'12px', borderTop:'1px solid #f1f5f9'}}>
                  <span style={{color:'#94a3b8', fontSize:'8px', fontStyle:'italic'}}>Prepared exclusively for Your Name</span>
                  <span style={{color:'#94a3b8', fontSize:'8px'}}>12</span>
                </div>
              </div>

            </div>

            {/* Gradient overlay + CTA */}
            <div 
              className="absolute bottom-0 left-0 right-0 z-10 flex items-end justify-center pb-8"
              style={{height:'40%', background:'linear-gradient(to top, #020617 0%, #020617ee 40%, transparent 100%)'}}
            >
              <button 
                onClick={() => document.getElementById('start')?.scrollIntoView({behavior:'smooth'})}
                className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-8 py-3 rounded-lg transition-colors shadow-lg shadow-blue-600/25"
              >
                Take the Assessment →
              </button>
            </div>
          </div>
        </div>

        {/* What's inside - section list */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3 max-w-md mx-auto">
          {[
            {n:'01', label:'Your Archetype'},
            {n:'02', label:'Primary Style'},
            {n:'03', label:'Secondary Influence'},
            {n:'04', label:'How Others See You'},
            {n:'05', label:'Blind Spots & Pressure'},
            {n:'06', label:'Growth Edge'},
            {n:'07', label:'Style Intensity Profile'},
            {n:'08', label:'Spot Their Style'},
            {n:'09', label:'Tactical Playbook'},
            {n:'10', label:'Shadow Assessment'},
            {n:'11', label:'What Comes Next'},
            {n:'📄', label:'16 Pages Total'},
          ].map(item=>(
            <div key={item.n} className="flex items-center gap-2 text-left">
              <span className="text-amber-500/70 text-[10px] font-bold tracking-wider min-w-[18px]">{item.n}</span>
              <span className="text-slate-400 text-xs">{item.label}</span>
            </div>
          ))}
        </div>

      </motion.div>
    </div>

    {/* ═══ CREDIBILITY ═══ */}
    <div className="bg-slate-950 px-6 py-20">
      <motion.div 
        initial={{opacity:0}} 
        whileInView={{opacity:1}} 
        viewport={{once:true}} 
        transition={{duration:0.8}} 
        className="max-w-2xl mx-auto text-center"
      >
        <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-14">
          <div className="flex flex-col items-center gap-1">
            <span className="text-3xl font-bold text-white">12+</span>
            <span className="text-xs uppercase tracking-widest text-slate-400">Years of Research</span>
          </div>
          <div className="hidden sm:block w-px h-12 bg-slate-700" />
          <div className="flex flex-col items-center gap-1">
            <span className="text-3xl font-bold text-white">12</span>
            <span className="text-xs uppercase tracking-widest text-slate-400">Unique Archetypes</span>
          </div>
          <div className="hidden sm:block w-px h-12 bg-slate-700" />
          <div className="flex flex-col items-center gap-1">
            <span className="text-3xl font-bold text-white">Cost</span>
            <span className="text-xs uppercase tracking-widest text-slate-400">US$ 19</span>
          </div>
        </div>

        <p className="text-slate-400 text-sm mt-8 max-w-md mx-auto leading-relaxed">
          Grounded in established behavioural science frameworks measured by psychologists and used by professional negotiators.
        </p>
      </motion.div>
    </div>
{/* ===== CREDIBILITY SECTION ===== */}
<section className="bg-slate-800 py-16 px-4">
  <div className="max-w-6xl mx-auto">
    <h2 className="text-white text-2xl md:text-3xl font-bold text-center mb-4">
      Built on established research
    </h2>
    <p className="text-slate-300 text-center max-w-3xl mx-auto mb-10">
      Negotiate Smarter is a research-informed assessment integrating
      <strong className="text-white"> negotiation psychology</strong>,
      <strong className="text-white"> conflict management theory</strong>,
      <strong className="text-white"> emotional dynamics in negotiation</strong>, and
      <strong className="text-white"> cross-cultural bargaining models</strong> to
      evaluate how professionals negotiate across routine and high-stakes contexts.
    </p>

    <h3 className="text-amber-200 font-semibold tracking-wide uppercase text-sm md:text-base mb-6">
      12 years of experiential research. 8 years of academic research including:
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-600 border border-slate-600 rounded-lg overflow-hidden">
      <div className="bg-slate-800 p-6">
        <h4 className="text-amber-200 font-bold uppercase tracking-wide text-xs mb-2">Conflict Management</h4>
        <p className="text-slate-300 text-sm leading-relaxed">Khan, K., Iqbal, Y. and Hussainy, S.K. (2016) Causes, effects, and remedies in conflict management. <em className="text-slate-200">The South East Asian Journal of Management</em>, 10(2), p.152.</p>
      </div>
      <div className="bg-slate-800 p-6">
        <h4 className="text-amber-200 font-bold uppercase tracking-wide text-xs mb-2">Negotiation Psychology</h4>
        <p className="text-slate-300 text-sm leading-relaxed">De Dreu, C.K., Beersma, B., Steinel, W. and Van Kleef, G.A. (2007) The psychology of negotiation: Principles and basic processes.</p>
      </div>
      <div className="bg-slate-800 p-6">
        <h4 className="text-amber-200 font-bold uppercase tracking-wide text-xs mb-2">Perspective-Taking</h4>
        <p className="text-slate-300 text-sm leading-relaxed">Ward, A., Disston, L.G., Brenner, L. and Ross, L. (2008) Acknowledging the other side in negotiation. <em className="text-slate-200">Negotiation Journal</em>, 24(3), pp.269-285.</p>
      </div>
      <div className="bg-slate-800 p-6">
        <h4 className="text-amber-200 font-bold uppercase tracking-wide text-xs mb-2">Conflict Resolution</h4>
        <p className="text-slate-300 text-sm leading-relaxed">Lim, J.H. and Yazdanifard, R. (2012) The difference of conflict management styles and conflict resolution in workplace. <em className="text-slate-200">Business & Entrepreneurship Journal</em>, 1(1).</p>
      </div>
      <div className="bg-slate-800 p-6">
        <h4 className="text-amber-200 font-bold uppercase tracking-wide text-xs mb-2">Emotions in Negotiation</h4>
        <p className="text-slate-300 text-sm leading-relaxed">Steinel, W., Van Kleef, G.A. and Harinck, F. (2008) Are you talking to me?! Separating the people from the problem when expressing emotions in negotiation. <em className="text-slate-200">Journal of Experimental Social Psychology</em>, 44(2), pp.362-369.</p>
      </div>
      <div className="bg-slate-800 p-6">
        <h4 className="text-amber-200 font-bold uppercase tracking-wide text-xs mb-2">Cross-Cultural Negotiation</h4>
        <p className="text-slate-300 text-sm leading-relaxed">Ghauri, P. and Fang, T. (2001) Negotiating with the Chinese: A socio-cultural analysis. <em className="text-slate-200">Journal of World Business</em>, 36(3), pp.303-325.</p>
      </div>
      <div className="bg-slate-800 p-6">
        <h4 className="text-amber-200 font-bold uppercase tracking-wide text-xs mb-2">Strategic Negotiation</h4>
        <p className="text-slate-300 text-sm leading-relaxed">Camp, J. and Jordan, R. (2020) <em className="text-slate-200">Start with NO.</em> Penguin Random House Audio Publishing Group.</p>
      </div>
      <div className="bg-slate-800 p-6">
        <h4 className="text-amber-200 font-bold uppercase tracking-wide text-xs mb-2">Tactical Empathy</h4>
        <p className="text-slate-300 text-sm leading-relaxed">Voss, C. and Raz, T. (2016) <em className="text-slate-200">Never split the difference: Negotiating as if your life depended on it.</em> Random House.</p>
      </div>
    </div>
  </div>
</section>
{/* ===== END CREDIBILITY ===== */}

    {/* ═══ CTA BOTTOM ═══ */}
    <div className="bg-white px-6 py-16 flex flex-col items-center">
      <motion.div 
        initial={{opacity:0, y:10}} 
        whileInView={{opacity:1, y:0}} 
        viewport={{once:true}}
        transition={{delay:0.2}} 
        className="text-center max-w-md w-full"
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
          Ready to Increase Your Deal Advantage?
        </h2>
        <p className="text-slate-500 text-base mb-8 max-w-sm mx-auto">
          7 minutes. 21 questions. A profile you'll reference before every important conversation.
        </p>

        <button
          onClick={() => document.getElementById('start')?.scrollIntoView({behavior:'smooth'})}
          className="mb-6 bg-blue-600 hover:bg-blue-500 text-white font-bold px-12 py-4 rounded-xl text-lg transition-all shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5 flex items-center justify-center gap-2 mx-auto"
        >
          Begin Assessment <ChevronRight className="w-5 h-5" />
        </button>

        <p className="text-sm text-slate-500 italic mb-10">
          Don't overthink it. Your first instinct is your truest answer.
        </p>

        <p className="text-xs text-slate-400 mt-8">
          &copy; 2026 The Buckingham Academy Limited
        </p>
      </motion.div>
    </div>

    {/* ═══ TEMP DEV BUTTON ═══ */}
    <button
      onClick={() => {
        const scores = { dominator: 2, integrator: 7, yielder: 2, calculator: 5 };
        const p = 'integrator', s = 'calculator';
        setResults({ scores, shadow: 3, primary: p, secondary: s, archetype: archetypes[p + '-' + s] });
        setPhase('results');
      }}
      className="fixed bottom-4 left-4 z-50 text-[10px] text-gray-400 hover:text-gray-200 bg-slate-800/80 px-2 py-1 rounded transition-colors"
    >
      ⚡ Dev
    </button>
</div>
);

if(phase==='quiz'){
    const q=questions[qi];
    const pct=((qi)/questions.length)*100;
   return(
      <>
        {/* Floating orbs background */}
        <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-slate-50 to-teal-50 overflow-hidden -z-10">
          <div className="orb orb-1"></div>
          <div className="orb orb-2"></div>
          <div className="orb orb-3"></div>
          <div className="orb orb-4"></div>
        </div>

        <div className="min-h-screen flex items-start justify-center p-4 sm:p-8">
          <div className="w-full max-w-2xl bg-white/75 backdrop-blur-xl rounded-3xl p-6 sm:p-10 border border-white/60 shadow-[0_8px_40px_rgba(0,0,0,0.06)]">
          <div className="mb-6">
  <div className="flex justify-between text-sm text-gray-500 mb-2">
    <span>Question {qi+1} of {questions.length}</span>
    <span>{Math.round(pct)}%</span>
  </div>
  <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
    <motion.div className="h-full bg-blue-700 rounded-full" initial={{width:0}} animate={{width:`${pct}%`}} transition={{duration:0.3}}/>
  </div>
<p className="text-base text-gray-600 mt-3 text-center leading-relaxed">
  Pick the answer closest to how you would actually behave.<br/>
  <span className="font-bold text-gray-900">NOT</span> how you think you should.
</p>
</div>
          <div className="flex flex-col justify-start pt-6">
            <AnimatePresence mode="wait">
              <motion.div key={qi} initial={{opacity:0,x:30}} animate={{opacity:1,x:0}} exit={{opacity:0,x:-30}} transition={{duration:0.2}}>
                <h2 className="text-xl sm:text-2xl font-semibold mb-6 text-gray-900 leading-relaxed">{q.text}</h2>
                <div className="space-y-3">
                  {q.options.map((o,i)=>(
                    <button key={i} onClick={()=>setSel(i)}
  className={`w-full text-left p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 flex items-start gap-3 hover:scale-[1.02] hover:shadow-md ${
    sel===i?'border-blue-700 bg-blue-50 text-blue-900 scale-[1.02] shadow-md':'border-grey-200 bg-white hover:border-blue-400 text-gray-700 hover:text-blue-900'
  }`}>
  <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
    sel===i?'bg-blue-700 text-white':'bg-gray-200 text-gray-500'
  }`}>{String.fromCharCode(65+i)}</span>
 <span className="text-base sm:text-lg">{o.text}</span>
</button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="flex justify-between mt-8 pb-4">
            <button onClick={back} disabled={qi===0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${qi===0?'text-gray-300 cursor-not-allowed':'text-gray-500 hover:text-gray-800'}`}>
              <ChevronLeft className="w-4 h-4"/>Back
            </button>
            <button onClick={next} disabled={sel===null}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-colors ${
                sel===null?'bg-gray-200 text-gray-400 cursor-not-allowed':'bg-blue-700 hover:bg-blue-800 text-white'
              }`}>
              {qi===questions.length-1?'See Results':'Next'}<ChevronRight className="w-4 h-4"/>
            </button>
          </div>
        </div>
        </div>
      </>
    );
  }

if(phase==='tiebreak'&&tieData) return(
  <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6">
    <h2 className="text-2xl font-bold text-gray-900 mb-2">One more question</h2>
    <p className="text-gray-500 mb-8">Your results are evenly split. Which description fits you best?</p>
    <div className="space-y-3 max-w-lg w-full">
      {tieData.tied.map(style=>(
        <button key={style} onClick={()=>{
          const sorted=Object.entries(tieData.scores).sort((a,b)=>
            b[1]-a[1] || (a[0]===style?-1:b[0]===style?1:0)
          );
          const p=sorted[0][0], s=sorted[1][0];
          const r={scores:tieData.scores,shadow:tieData.shadow,primary:p,secondary:s,archetype:archetypes[p+'-'+s]};
          setResults(r);
          setPhase('results');
        }}
          className="w-full text-left p-5 rounded-lg border-2 border-gray-200 hover:border-blue-700 hover:bg-blue-50 transition-all">
          <span className="font-bold" style={{color:styleMeta[style].color}}>{styleMeta[style].label}</span>
          <span className="text-gray-500 text-sm ml-2">
            {style==='dominator'&&'— I negotiate with directness to win the best deal possible'}
            {style==='integrator'&&'— I focus on collaboration and creative problem-solving'}
            {style==='yielder'&&'— I focus on empathasing with the other party and relationship-building'}
            {style==='calculator'&&'— I negotiate using thorough preparation and analysis'}
          </span>
        </button>
      ))}
    </div>
  </div>
);


if(phase==='results'&&results){
    const{scores:sc,shadow:sh,primary:p,secondary:s,archetype:arch}=results;
    const sl=shadowLevels[sh];

    const navy='#1E2A3E';
    const gold='#C9A84C';

    const renderParagraphs=(text)=>text.split('\n\n').map((para,i)=><p key={i} className={`text-gray-600 leading-relaxed text-sm ${i>0?'mt-4':''}`}>{para}</p>);

    const SectionLabel=({number,text})=>(
      <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{color:gold}}>
        {number && <>{number} &mdash; </>}{text}
      </p>
    );

    const SectionTitle=({children})=>(
      <h2 className="font-serif text-2xl sm:text-3xl font-bold mb-4" style={{color:navy}}>{children}</h2>
    );

    const Divider=()=>(
      <div className="border-t border-gray-200 my-8"></div>
    );

    return(
     <div id="report" className="min-h-screen bg-white text-gray-900 overflow-y-auto">
<style>{`
#report .bg-gray-800 {
  background-color: transparent !important;
  background-image: linear-gradient(to right, #1f2937, #d1d5db) !important;
}
    #report .text-sm { font-size: 1rem; line-height: 1.75; }
    #report .text-xs { font-size: 0.875rem; line-height: 1.5; }
    #report .text-base { font-size: 1.125rem; line-height: 1.75; }
    #report .text-2xl { font-size: 1.875rem; }
    #report .text-3xl { font-size: 2.25rem; }
`}</style>
  <div className="max-w-3xl mx-auto">

          <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{duration:0.6}}
            className="px-6 py-12 sm:py-16" style={{backgroundColor:navy}}>
            <div className="flex items-center justify-between mb-8">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">The Buckingham Academy</p>
              <p className="text-xs font-medium text-gray-500 border border-gray-600 px-2 py-0.5">Confidential</p>
            </div>
            <div className="w-10 h-0.5 mb-8" style={{backgroundColor:gold}}></div>
            {userName && <p className="text-gray-400 text-sm mb-1">Prepared for</p>}
            {userName && <p className="text-white font-medium mb-6">{userName}</p>}
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{color:gold}}>Your Negotiation Archetype</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-3">{arch.name}</h1>
            <p className="text-gray-300 text-base italic">{arch.tagline}</p>
            <div className="flex items-center gap-3 mt-6">
              <div className="px-3 py-1 border border-gray-500 rounded text-xs text-gray-300">
                <span className="text-gray-500 mr-1">Primary</span> <span className="font-bold text-white">{styleMeta[p].label}</span>
              </div>
              <div className="px-3 py-1 border border-gray-500 rounded text-xs text-gray-300">
                <span className="text-gray-500 mr-1">Secondary</span> <span className="font-bold text-white">{styleMeta[s].label}</span>
              </div>
            </div>
          </motion.div>

          <div className="px-6 py-10">
            {/* Top Download Button */}
<motion.div 
  initial={{opacity:0, scale:0.9}} 
  animate={{opacity:1, scale:1}} 
  transition={{duration:0.5, ease:"easeOut"}} 
  className="fixed inset-0 z-50 flex items-center justify-center p-6"
  style={{background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"}}
>
  <div className="bg-white rounded-2xl p-12 max-w-md w-full text-center shadow-2xl">
    
    {/* Brand */}
    <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{color: navy}}>
      THE BUCKINGHAM ACADEMY
    </p>
    <div className="w-12 h-0.5 mx-auto mb-8 rounded" style={{background: "#c9a84c"}}></div>

    {/* Checkmark */}
    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse" 
         style={{background: "#fdf9ef"}}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    </div>

    {/* Headline */}
    <h1 className="text-3xl font-bold mb-2" style={{color: navy}}>
      Your Report is Ready!
    </h1>
    <p className="text-gray-500 mb-10">
      Your personalized negotiation profile has been generated.
    </p>

    {/* Giant Pulsing Download Button */}
    <style>{`
      @keyframes btnPulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(243, 135, 53, 0.45); }
        50% { box-shadow: 0 0 0 14px rgba(201,168,76,0); }
      }
    `}</style>
    <button 
      onClick={download} 
      className="flex items-center justify-center gap-3 text-white font-bold px-12 py-5 rounded-xl text-xl mx-auto transition-all hover:-translate-y-1 cursor-pointer"
      style={{
        backgroundColor: navy,
        animation: "btnPulse 2s ease-in-out infinite"
      }}
    >
      <Download className="w-6 h-6"/>
      Download Report
    </button>
    <p className="text-sm text-gray-400 mt-3">PDF • Instant download</p>

    {/* Warning */}
    <div className="flex items-center gap-3 text-left mt-8 p-4 rounded-xl border" 
         style={{background: "#fdf9ef", borderColor: "#e8d8a8", color: "#7a6523"}}>
      <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="#c9a84c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <span className="text-sm">
        <strong>Download now:</strong> your results cannot be retrieved after leaving this page.
      </span>
    </div>

  </div>
</motion.div>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.2}} className="pdf-avoid-break">
              <SectionLabel number="01" text="YOUR NEGOTIATION ARCHETYPE"/>
              <SectionTitle>{arch.name}</SectionTitle>
              <p className="text-base italic text-gray-500 mb-6">{arch.tagline}</p>

              <div className="space-y-3 mb-6">
                {[
                  {k:'integrator',label:'Integrator'},
                  {k:'calculator',label:'Calculator'},
                  {k:'dominator',label:'Dominator'},
                  {k:'yielder',label:'Yielder'},
                ].sort((a,b)=>sc[b.k]-sc[a.k]).map((a)=>{
                  const percent=Math.round((sc[a.k]/16)*100);
                  const level=getStyleLevel(sc[a.k]);
                  const lbl=levelLabels[level];
                  return(
                    <div key={a.k}>
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-sm font-medium text-gray-800">{a.label}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold" style={{color:navy}}>{percent}%</span>
                          <span className="text-xs text-gray-400 uppercase">{lbl.text}</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{backgroundColor:navy}}
                          initial={{width:0}}
                          animate={{width:percent+'%'}}
                          transition={{duration:0.8,delay:0.4}}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-gray-50 border-l-2 p-5 mt-6" style={{borderLeftColor:navy}}>
                {renderParagraphs(arch.narrative)}
              </div>
            </motion.div>

            <Divider/>



            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.3}} className="pdf-avoid-break">
              <SectionLabel number="02" text="YOUR PRIMARY STYLE"/>
              <SectionTitle>{styleMeta[p].label}</SectionTitle>
              <p className="text-sm text-gray-500 mb-4">Your dominant negotiation approach &mdash; the pattern you default to under pressure.</p>
              <div className="border-l-2 pl-4 mb-5" style={{borderLeftColor:navy}}>
                <p className="text-base font-medium text-gray-800 italic leading-relaxed">
                  {stylePrimary[p].split('. ')[0]}.
                </p>
              </div>
              <div className="text-sm text-gray-600 leading-relaxed space-y-4">
                {stylePrimary[p].split('\n\n').map((para,i)=><p key={i}>{para}</p>)}
              </div>
            </motion.div>

            <Divider/>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.35}} className="pdf-avoid-break">
              <SectionLabel number="03" text="YOUR SECONDARY INFLUENCE"/>
              <SectionTitle>{styleMeta[s].label}</SectionTitle>
              <p className="text-sm text-gray-500 mb-4">The supporting style that shapes how your primary approach is expressed.</p>
              <div className="border-l-2 pl-4 mb-5" style={{borderLeftColor:gold}}>
                <p className="text-base font-medium text-gray-800 italic leading-relaxed">
                  {styleSecondary[s].split('. ')[0]}.
                </p>
              </div>
              {renderParagraphs(styleSecondary[s])}
            </motion.div>

            <Divider/>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.4}} className="pdf-avoid-break">
              <SectionLabel number="04" text="HOW OTHERS EXPERIENCE YOU"/>
              <SectionTitle>Your Negotiation Presence</SectionTitle>
              <p className="text-sm text-gray-500 mb-6">What counterparts perceive when they sit across the table from you.</p>
              {renderParagraphs(arch.howOthersSeeYou)}
            </motion.div>

            <Divider/>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.45}} className="pdf-avoid-break">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-gray-800 mb-4 pb-2 border-b border-gray-200">Strengths</h4>
                  <div className="space-y-3">
                    {arch.strengths.split('. ').filter((str)=>str.trim().length>10).map((str,i)=>(
                      <div key={i} className="flex gap-2 items-start">
                        <span className="shrink-0 text-gray-800 mt-0.5 font-bold">&#10003;</span>
                        <p className="text-sm text-gray-600 leading-relaxed">{str.trim().replace(/\.$/,'')}.</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-gray-800 mb-4 pb-2 border-b border-gray-200">Weaknesses</h4>
                  <div className="space-y-3">
                    {arch.weaknesses.split('. ').filter((str)=>str.trim().length>10).map((str,i)=>(
                      <div key={i} className="flex gap-2 items-start">
                        <span className="shrink-0 text-gray-800 mt-0.5 font-bold">&#10007;</span>
                        <p className="text-sm text-gray-600 leading-relaxed">{str.trim().replace(/\.$/,'')}.</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            <Divider/>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.5}} className="pdf-avoid-break">
              <SectionLabel number="05" text="VULNERABILITIES"/>
              <SectionTitle>Blind Spots &amp; Pressure Points</SectionTitle>
              <p className="text-sm text-gray-500 mb-6">Where your natural patterns may work against you.</p>

              <div className="bg-gray-50 rounded p-5 mb-5">
                <h4 className="text-sm font-bold text-gray-800 mb-3">Your Blind Spots</h4>
                {renderParagraphs(arch.blindSpots)}
              </div>

              <div className="bg-gray-50 rounded p-5">
                <h4 className="text-sm font-bold text-gray-800 mb-3">Under Pressure</h4>
                {renderParagraphs(arch.underPressure)}
              </div>
            </motion.div>

            <Divider/>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.55}} className="pdf-avoid-break">
              <SectionLabel number="06" text="DEVELOPMENT"/>
              <SectionTitle>Growth Edge</SectionTitle>
              <p className="text-sm text-gray-500 mb-6">Your path to becoming a more complete negotiator.</p>

              <div className="bg-gray-50 rounded p-5 mb-5">
                <h4 className="text-sm font-bold text-gray-800 mb-3">Watch Out</h4>
                {renderParagraphs(arch.watchOut)}
              </div>

              <div className="bg-gray-50 rounded p-5 mb-5">
                <h4 className="text-sm font-bold text-gray-800 mb-3">Your Growth Edge</h4>
                {renderParagraphs(arch.growthEdge)}
              </div>

              <div className="space-y-3 mt-6">
                {arch.growthSteps.map((step,i)=>(
                  <div key={i} className="flex gap-4 items-start">
                    <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs text-white" style={{backgroundColor:navy}}>{i+1}</div>
                    <p className="text-sm text-gray-700 leading-relaxed pt-1">{step}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <Divider/>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.6}} className="pdf-break-before pdf-avoid-break">
              <SectionLabel number="07" text="STYLE INTENSITY PROFILE"/>
              <SectionTitle>Detailed Breakdown</SectionTitle>
              <p className="text-sm text-gray-500 mb-6">How strongly each negotiation style influences your behaviour at the table.</p>

              <div className="space-y-6">
                {['dominator','integrator','yielder','calculator'].map((style)=>{
                  const score=sc[style];
                  const level=getStyleLevel(score);
                  const meta=styleMeta[style];
                  const lbl=levelLabels[level];
                  const pct=Math.round((score/16)*100);
                  return(
                    <div key={style} className="border border-gray-200 rounded p-5 pdf-avoid-break">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-sm text-gray-900">{meta.label}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500">{lbl.text}</span>
                          <span className="text-sm font-bold text-gray-900">{pct}%</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
                        <motion.div
                          className="h-full rounded-full"
                          style={{backgroundColor:navy}}
                          initial={{width:0}}
                          animate={{width:pct+'%'}}
                          transition={{duration:0.8,delay:0.4}}
                        />
                      </div>
                      <p className="text-sm font-medium text-gray-800 mb-2">{meta.brief}</p>
                      <p className="text-sm text-gray-600 leading-relaxed">{styleLevels[style][level]}</p>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <Divider/>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.65}} className="pdf-break-before">
              <SectionLabel number="08" text="READING THE ROOM"/>
              <SectionTitle>Spot Their Style</SectionTitle>
              <p className="text-sm text-gray-500 mb-6">Identify your counterpart&apos;s negotiation approach. Look for behaviour trends over time rather than just first impressions as people often play different characters at the beginning of an engagement. You might find the warm, smiley individual you just met is actually disguising their true tough intentions.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {['dominator','integrator','yielder','calculator'].map((style)=>{
                  const sg=spottingGuide[style];
                  const sm=styleMeta[style];
                  return(
                    <div key={style} className="border border-gray-200 rounded p-4 pdf-avoid-break">
                      <div className="font-bold text-sm mb-3 pb-2 border-b border-gray-100" style={{color:navy}}>{sm.label}</div>
                      <div className="space-y-2 text-xs text-gray-700">
                        <div className="flex gap-2"><span className="font-semibold text-gray-500 w-12 shrink-0">Pace:</span><span>{sg.pace}</span></div>
                        <div className="flex gap-2"><span className="font-semibold text-gray-500 w-12 shrink-0">Tone:</span><span>{sg.tone}</span></div>
                        <div className="flex gap-2"><span className="font-semibold text-gray-500 w-12 shrink-0">Focus:</span><span>{sg.focus}</span></div>
                        <div className="mt-3">
                          <span className="font-semibold text-gray-500 uppercase text-xs tracking-wide">You will hear:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {sg.phrases.map((ph,i)=><span key={i} className="px-2 py-0.5 rounded border border-gray-200 text-xs font-medium text-gray-700 bg-gray-50">{ph}</span>)}
                          </div>
                        </div>
                        <div className="mt-2">
                          <span className="font-semibold text-gray-500 uppercase text-xs tracking-wide">They will:</span>
                          <div className="mt-1">{sg.behaviours.map((b,i)=><div key={i} className="text-gray-600">&bull; {b}</div>)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <Divider/>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.7}} className="pdf-break-before">
              <SectionLabel number="09" text="YOUR TACTICAL PLAYBOOK"/>
              <SectionTitle>Style Matchups</SectionTitle>
              <p className="text-sm text-gray-500 mb-6">How to adapt your approach when facing each opposing style.</p>

              <div className="space-y-6">
                {['dominator','integrator','yielder','calculator']
                  .filter((style)=>style!==p)
                  .map((opponent,i)=>{
                    const key=p+'-'+opponent;
                    const m=matchupAdvice[key];
                    const oppMeta=styleMeta[opponent];
                    const myMeta=styleMeta[p];
                    return(
                      <div key={key} className={`${i>0?'pdf-break-before':''} border border-gray-200 rounded overflow-hidden pdf-avoid-break`}>
                        <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                          <span className="font-bold text-sm" style={{color:navy}}>You ({myMeta.label})</span>
                          <span className="text-gray-400 text-sm mx-2 italic">vs</span>
                          <span className="font-bold text-sm" style={{color:navy}}>{oppMeta.label}</span>
                        </div>

                        <div className="p-5 space-y-5">
                          <div>
                            <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Their Tells</h5>
                            <div className="space-y-1">{m.tells.map((t,idx)=><div key={idx} className="text-sm text-gray-700 flex gap-2"><span className="text-gray-300">&bull;</span><span>{t}</span></div>)}</div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <h5 className="text-xs font-bold uppercase tracking-wider mb-2" style={{color:navy}}>Your Advantages</h5>
                              <div className="space-y-1">{m.advantages.map((a,idx)=><div key={idx} className="text-sm text-gray-700 flex gap-2"><span className="text-gray-800 font-bold">&#10003;</span><span>{a}</span></div>)}</div>
                            </div>
                            <div>
                              <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Your Risks</h5>
                              <div className="space-y-1">{m.risks.map((r,idx)=><div key={idx} className="text-sm text-gray-700 flex gap-2"><span className="text-gray-800 font-bold">&#10007;</span><span>{r}</span></div>)}</div>
                            </div>
                          </div>

                          <div>
                            <h5 className="text-xs font-bold uppercase tracking-wider mb-2" style={{color:gold}}>Your Playbook</h5>
                            <div className="space-y-1">{m.playbook.map((step,idx)=><div key={idx} className="text-sm text-gray-700 flex gap-2"><span className="font-bold" style={{color:navy}}>{idx+1}.</span><span>{step}</span></div>)}</div>
                          </div>

                          <div className="bg-gray-50 border border-gray-200 rounded p-4">
                            <h5 className="text-xs font-bold uppercase tracking-wider mb-1" style={{color:gold}}>Watch For The Fake Style</h5>
                            <p className="text-sm text-gray-600 leading-relaxed">{m.fakeWarning}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div className="mt-6 p-5 rounded" style={{backgroundColor:navy}}>
                <h5 className="font-bold text-sm mb-2 text-white">The Most Important Rule</h5>
                <p className="text-sm leading-relaxed text-gray-300">The most dangerous negotiator is not the one who is aggressive. It is the one who is pretending to be something they are not. If someone&apos;s words say collaboration but their proposals say competition, trust the proposals. If their warmth appeared suddenly and conveniently, question what it is designed to achieve. Behaviour reveals intention far more reliably than language ever will.</p>
              </div>
            </motion.div>

            <Divider/>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.75}} className="pdf-break-before pdf-avoid-break">
              <SectionLabel number="10" text="SHADOW ASSESSMENT"/>
              <SectionTitle>{sl.title}</SectionTitle>
              <p className="text-sm text-gray-500 mb-4">{sl.sub}</p>

              <div className="flex items-center gap-2 mb-6">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-2">Shadow Score</span>
                {[0,1,2,3,4].map((i)=>(
                  <div key={i} className="w-5 h-5 rounded-full" style={{backgroundColor:i<sh?gold:'#E5E7EB'}}></div>
                ))}
                <span className="text-sm font-bold ml-2" style={{color:navy}}>{sh} / 5</span>
              </div>

              {renderParagraphs(sl.msg)}
            </motion.div>

            <Divider/>

            <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.8}} className="pdf-avoid-break">
              <SectionLabel number="11" text="WHAT COMES NEXT"/>
              <SectionTitle>Using This Report</SectionTitle>
              <p className="text-sm text-gray-500 mb-4">Your profile is a starting point, not a label.</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">This report maps your current negotiation tendencies based on how you responded under simulated pressure. It reflects your defaults &mdash; the patterns you fall into when you have not consciously chosen a different approach. Defaults are not destiny. They are simply what happens when you are not paying attention.</p>
              <p className="text-sm text-gray-600 leading-relaxed">The most effective negotiators are not locked into one style. They understand their natural inclinations, recognise when those inclinations serve them, and develop the ability to shift when circumstances demand something different.</p>
            </motion.div>

            {emailStatus==='sending' && (
              <p className="text-sm text-gray-500 animate-pulse mt-8 text-center">Sending report to {userEmail}...</p>
            )}
            {emailStatus==='sent' && (
              <p className="text-sm text-green-700 mt-8 text-center">Report sent to {userEmail}</p>
            )}
            {emailStatus==='failed' && (
              <div className="no-pdf text-center mt-8">
                <p className="text-sm text-red-500">Failed to send email. Use the download button instead.</p>
                <button onClick={sendReportEmail} className="text-sm underline mt-1" style={{color:navy}}>Try again</button>
              </div>
            )}

            <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.9}} className="flex flex-col items-center gap-3 mt-10 mb-6">
              <button onClick={download} className="flex items-center gap-2 text-white font-semibold px-8 py-3 rounded text-base transition-all hover:opacity-90 shadow-sm" style={{backgroundColor:navy}}>
                <Download className="w-4 h-4"/>Download Report
              </button>
            </motion.div>

            <div className="text-center text-xs text-gray-400 mt-10 pt-6 border-t border-gray-200 pb-8">
              <p className="font-bold mb-1" style={{color:navy}}>The Buckingham Academy</p>
              <p className="text-gray-500 mb-1">We coach teams to close deals.</p>
              <p>admin@bucademy.com</p>
              <p className="mt-3">&copy; 2026 The Buckingham Academy Limited. All rights reserved.</p>
            </div>

          </div>
        </div>
      </div>
    );
  }

  return null;
}